import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Download,
  Trash2,
  Loader2,
  AlertTriangle,
  Plus,
  UploadCloud,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  Brain,
  Users,
  Sparkles,
  Camera,
  MessageSquare,
  Shield,
  Heart,
  Eye,
  EyeOff,
  Palette,
  Copy,
  Anchor,
  Sun,
  Grid,
  CheckCircle,
  Activity,
  Layers,
  Zap,
  Target,
  Fingerprint,
  Lightbulb,
  Compass,
  Scale,
  BookOpen,
  Crosshair,
  Lock,
  MousePointer2,
  BrainCircuit,
  Aperture,
  Maximize,
  Sliders,
  TrendingUp,
  AlertCircle,
  Mic2,
  Focus,       
  UserCheck,
  Quote
} from 'lucide-react';
import { auth, getStorageURL, uploadBytesToStorage } from '../firebase.js';

// ====================================================================================
// HELPERS & UI COMPONENTS
// ====================================================================================

const formatText = (text) => {
  if (typeof text !== 'string' || !text) return 'N/A';
  return text
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ScoreBar = ({ label, value, colorClass = "bg-cyan-500", showValue = true, max = 1 }) => {
  const normalizedValue = (value || 0) / max;
  const percentage = Math.min(Math.max(normalizedValue * 100, 0), 100);
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400 font-medium">{label}</span>
        {showValue && <span className="text-slate-200 font-mono">{Math.round(percentage)}%</span>}
      </div>
      <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${colorClass} shadow-[0_0_10px_rgba(0,0,0,0.3)] transition-all duration-500`} 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const PolarityMeter = ({ label, value }) => {
  const percentage = Math.min(Math.max(value || 0, -1), 1); 
  const leftPos = percentage < 0 ? 50 + (percentage * 50) : 50;
  const width = Math.abs(percentage * 50);
  
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400 font-medium">{label}</span>
        <span className={`font-mono ${value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : 'text-slate-400'}`}>
          {value > 0 ? '+' : ''}{value?.toFixed(2)}
        </span>
      </div>
      <div className="relative h-2 w-full bg-slate-700/50 rounded-full overflow-hidden flex items-center">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-500 z-10" />
        <div 
          className={`absolute h-full transition-all duration-500 ${percentage > 0 ? 'bg-green-500' : 'bg-red-500'}`}
          style={{ left: `${leftPos}%`, width: `${width}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-0.5 px-0.5">
        <span>Neg</span>
        <span>Pos</span>
      </div>
    </div>
  );
};

const Tag = ({ children, color = "bg-slate-700 text-slate-200" }) => (
  <span className={`px-2 py-0.5 ${color} text-xs rounded border border-white/5 font-medium whitespace-nowrap`}>
    {children}
  </span>
);

const PentadDisplay = ({ pentad }) => {
  if (!pentad) return null;
  
  const items = [
    { label: "Act", value: pentad.act, icon: <Activity size={12} className="text-cyan-400" /> },
    { label: "Agent", value: pentad.agent, icon: <Users size={12} className="text-purple-400" /> },
    { label: "Scene", value: pentad.scene, icon: <ImageIcon size={12} className="text-green-400" /> },
    { label: "Agency", value: pentad.agency, icon: <Layers size={12} className="text-orange-400" /> },
    { label: "Purpose", value: pentad.purpose, icon: <Sparkles size={12} className="text-pink-400" /> },
  ];

  return (
    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 mt-4">
      <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
        <Anchor size={14} className="mr-2 text-indigo-400" /> Burke's Dramatistic Pentad
      </h3>
      <div className="grid grid-cols-1 gap-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/50 rounded border border-slate-700/30">
            <div className="flex items-center gap-2">
              {item.icon}
              <span className="text-xs text-slate-400 font-medium uppercase">{item.label}</span>
            </div>
            <span className="text-sm text-slate-200 font-semibold">{formatText(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// === NEW COMPONENT: GEOMETRY & BALANCE ===
const GeometryAndBalance = ({ selectedImage, signedDisplayUrl, formatText }) => {
  const [showOverlay, setShowOverlay] = useState(false);

  return (
    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center">
          <Grid size={14} className="mr-2 text-purple-400" /> Geometry & Balance
        </h3>
        
        {/* Toggle Button */}
        {signedDisplayUrl && ( 
          <button
            onClick={() => setShowOverlay(!showOverlay)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
              showOverlay 
                ? "bg-cyan-900/40 text-cyan-300 border border-cyan-700/50" 
                : "bg-slate-700 hover:bg-slate-600 text-slate-300 border border-transparent"
            }`}
          >
            {showOverlay ? (
              <>
                <EyeOff size={12} /> Hide Overlay
              </>
            ) : (
              <>
                <Eye size={12} /> Show Overlay
              </>
            )}
          </button>
        )}
      </div>
            
      <div className="flex justify-between mb-4">
        <div>
          <span className="text-xs text-slate-400 block mb-1">Archetype</span>
          <span className="px-2 py-1 bg-purple-900/30 text-purple-200 text-xs rounded font-bold border border-purple-800/50">
            {formatText(selectedImage.composition_archetype || "Unstructured")}
          </span>
        </div>
        <div className="text-right">
          <span className="text-xs text-slate-400 block mb-1">Balance Type</span>
          <span className="text-xs font-medium text-white">
            {formatText(selectedImage.visual_balance_type || "Neutral")}
          </span>
        </div>
      </div>

      {/* Visual Center of Mass */}
{selectedImage.visual_center_of_mass && (
  <>
    <div className="relative w-full h-40 bg-slate-900 rounded border border-slate-700/50 overflow-hidden mb-2 group">
      
      {/* 1. Background Image (when overlay is active) */}
      {showOverlay && signedDisplayUrl && (  
        <div className="absolute inset-0 z-0">
          <img
            src={signedDisplayUrl}  
            alt="Composition overlay"
            className="w-full h-full object-cover opacity-40 grayscale-[20%]"
          />
        </div>
      )}
                
      {/* 2. Grid Lines (Rule of Thirds) */}
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none z-10">
        <div className="border-r border-b border-white/10"></div>
        <div className="border-r border-b border-white/10"></div>
        <div className="border-b border-white/10"></div>
        <div className="border-r border-b border-white/10"></div>
        <div className="border-r border-b border-white/10"></div>
        <div className="border-b border-white/10"></div>
        <div className="border-r border-white/10"></div>
        <div className="border-r border-white/10"></div>
        <div></div>
      </div>
                
      {/* 3. Center Cross Lines */}
      <div className="absolute inset-0 pointer-events-none z-10 opacity-50">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-cyan-500/20" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-cyan-500/20" />
      </div>
                
      {/* 4. The Center of Mass Dot */}
      <div 
         className="absolute z-20 transition-all duration-700 ease-out"
         style={{ 
           left: `${(selectedImage.visual_center_of_mass.x || 0.5) * 100}%`, 
           top: `${(selectedImage.visual_center_of_mass.y || 0.5) * 100}%` 
         }}
      >
         <div className="absolute transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-3 h-3 bg-cyan-400 rounded-full border-2 border-white shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>
            <div className="absolute inset-0 w-3 h-3 bg-cyan-400 rounded-full animate-ping opacity-75"></div>
        </div>
        
        {/* Tooltip on Hover */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/80 text-[9px] text-cyan-300 px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none border border-cyan-900/50">
            COM ({selectedImage.visual_center_of_mass.x?.toFixed(2)}, {selectedImage.visual_center_of_mass.y?.toFixed(2)})
        </div>
      </div>
    </div>
    
    {/* Explanation Text */}
    <p className="text-[9px] text-slate-500 italic leading-relaxed mb-3">
      The dot represents the visual "center of mass" - the point where your eye is naturally drawn based on the weighted distribution of visual elements, colors, and contrast. 
      Positioned at ({(selectedImage.visual_center_of_mass.x * 100).toFixed(0)}%, {(selectedImage.visual_center_of_mass.y * 100).toFixed(0)}%) from top-left, 
      this reveals whether the composition pulls attention toward the center, edges, or follows compositional rules like the golden ratio.
    </p>
  </>
)}
      
            
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-slate-900/50 p-2 rounded flex justify-between items-center border border-slate-800">
          <span className="text-slate-500">Rule of Thirds</span>
          {selectedImage.follows_rule_of_thirds ? 
             <CheckCircle size={12} className="text-green-500"/> : 
             <X size={12} className="text-slate-600"/>}
        </div>
        <div className="bg-slate-900/50 p-2 rounded flex justify-between items-center border border-slate-800">
          <span className="text-slate-500">Golden Ratio</span>
          {selectedImage.follows_golden_ratio ? 
             <CheckCircle size={12} className="text-green-500"/> : 
             <X size={12} className="text-slate-600"/>}
        </div>
      </div>
            
      {/* Balance Scores */}
      {(selectedImage.horizontal_balance !== undefined) && (
        <div className="space-y-2 pt-2 border-t border-slate-700/30">
          {selectedImage.horizontal_balance !== undefined && (
             <div className="flex items-center gap-2 text-[10px] text-slate-400">
                <span className="w-16">Horz. Balance</span>
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                     className="h-full bg-purple-500 rounded-full"
                     style={{ width: `${selectedImage.horizontal_balance * 100}%` }}
                  />
                </div>
                <span className="w-6 text-right font-mono text-white">{selectedImage.horizontal_balance.toFixed(1)}</span>
             </div>
          )}
          {selectedImage.vertical_balance !== undefined && (
             <div className="flex items-center gap-2 text-[10px] text-slate-400">
                <span className="w-16">Vert. Balance</span>
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                     className="h-full bg-purple-500 rounded-full"
                     style={{ width: `${selectedImage.vertical_balance * 100}%` }}
                  />
                </div>
                <span className="w-6 text-right font-mono text-white">{selectedImage.vertical_balance.toFixed(1)}</span>
             </div>
          )}
        </div>
      )}
    </div>
  );
};

// === POWER GEOMETRY COMPONENT (WITH MICROEXPRESSION SYNTHESIS) ===
const PowerGeometryAnalysis = ({ selectedImage }) => {
  const facs = selectedImage.facs_analysis;
  if (!facs?.angle_of_observation) return null;
  
  const angleData = facs.angle_of_observation;
  const cameraAngle = angleData.camera_angle_analysis?.camera_angle || 0;
  const powerGeo = angleData.power_geometry;
  
  const detectedEmotion = selectedImage.dominant_emotion || 'neutral';
  const microexpressions = selectedImage.microexpressions_filtered || selectedImage.microexpressions || [];
  const emotionEffects = powerGeo?.emotion_specific_effects || {};
  
  // Find which category this emotion belongs to
  const getEmotionCategory = (emotion) => {
    for (const [category, data] of Object.entries(emotionEffects)) {
      if (data.emotions?.some(e => e.toLowerCase() === emotion.toLowerCase())) {
        return { category, data };
      }
    }
    return null;
  };
  
  const dominantEmotionCategory = getEmotionCategory(detectedEmotion);
  
  // Parse microexpressions that have angle-adjusted confidence in FACS data
  const getMicroexpressionImpact = () => {
    const impacts = [];
    
    // Check each detected microexpression for angle impact
    microexpressions.forEach(micro => {
      const microKey = micro.toLowerCase();
      const facsData = facs[microKey];
      
      if (facsData?.power_geometry) {
        const originalConf = facsData.power_geometry.original_confidence || 0;
        const adjustedConf = facsData.power_geometry.angle_adjusted_confidence || 0;
        const effectCategory = facsData.power_geometry.effect_category;
        const multiplier = facsData.power_geometry.multiplier_applied || 1;
        
        if (Math.abs(adjustedConf - originalConf) > 0.05) {
          impacts.push({
            name: micro,
            original: originalConf,
            adjusted: adjustedConf,
            change: adjustedConf - originalConf,
            category: effectCategory,
            multiplier: multiplier
          });
        }
      }
    });
    
    return impacts.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
  };
  
  const microImpacts = getMicroexpressionImpact();
  
  // Generate PERCEPTION-FOCUSED narrative
  const generatePerceptionNarrative = () => {
    const effect = powerGeo?.psychological_effect || '';
    const multiplier = powerGeo?.confidence_adjusted_multiplier || 1;
    const valenceShift = powerGeo?.valence_shift || 0;
    
    let narrative = '';
    
    if (Math.abs(cameraAngle) < 5) {
      narrative = `Viewers see the subject as an **equal peer**. The eye-level framing creates conversational intimacy—neither looking up (deference) nor looking down (judgment). This builds trust through parity, making the subject feel accessible and relatable.`;
    } else if (cameraAngle < 0) {
      const intensity = Math.abs(cameraAngle) > 20 ? 'dramatically' : Math.abs(cameraAngle) > 10 ? 'noticeably' : 'subtly';
      
      narrative = `Viewers unconsciously perceive the subject as **${intensity} less powerful**. Looking down creates "${formatText(effect)}"—the subject appears smaller, more vulnerable, and less threatening. `;
      
      if (dominantEmotionCategory) {
        if (dominantEmotionCategory.category === 'high_power_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion is undermined: what should read as confident instead feels forced or performative. This angle reduces leadership presence by ${Math.round((1 - multiplier) * 100)}%, potentially damaging authority positioning.`;
        } else if (dominantEmotionCategory.category === 'low_power_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion is reinforced authentically: viewers read genuine vulnerability and approachability. This angle amplifies empathy by ${Math.round((multiplier - 1) * 100)}%, ideal for humanizing or building emotional connection.`;
        } else if (dominantEmotionCategory.category === 'transformable_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion transforms into **${dominantEmotionCategory.data.high_angle_effect}** in viewer perception—shifting interpretation toward introspection, humility, or contemplation rather than authority.`;
        }
      }
      
      narrative += ` ${valenceShift < 0 ? 'Emotional warmth decreases' : 'Emotional engagement shifts'} by ${Math.abs(valenceShift * 100).toFixed(0)}%.`;
      
    } else {
      const intensity = Math.abs(cameraAngle) > 20 ? 'dramatically' : Math.abs(cameraAngle) > 10 ? 'significantly' : 'subtly';
      
      narrative = `Viewers unconsciously perceive the subject as **${intensity} more powerful**. Looking up triggers automatic deference—the subject appears larger, more imposing, and commanding. `;
      
      if (dominantEmotionCategory) {
        if (dominantEmotionCategory.category === 'high_power_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion is amplified: confidence becomes dominance, determination becomes unstoppable will. This angle enhances authority presence by ${Math.round((multiplier - 1) * 100)}%, ideal for leadership positioning but risks appearing distant or unapproachable.`;
        } else if (dominantEmotionCategory.category === 'low_power_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion creates cognitive dissonance: vulnerability from below feels inauthentic or manipulative. This contradictory framing reduces emotional credibility by ${Math.round((1 - multiplier) * 100)}%, potentially backfiring with sophisticated audiences.`;
        } else if (dominantEmotionCategory.category === 'transformable_emotions') {
          narrative += `The detected **${detectedEmotion}** emotion transforms into **${dominantEmotionCategory.data.low_angle_effect}** in viewer perception—shifting interpretation toward stoic authority, gravitas, or commanding presence.`;
        }
      }
      
      narrative += ` ${valenceShift > 0 ? 'Emotional impact intensifies' : 'Emotional tone shifts'} by ${Math.abs(valenceShift * 100).toFixed(0)}%.`;
    }
    
    return narrative;
  };
  
  // Strategic recommendation
  const getStrategicRecommendation = () => {
    if (Math.abs(cameraAngle) < 5) {
      return {
        verdict: 'Optimal for trust-building',
        color: 'text-green-400',
        icon: '✓',
        advice: 'Use for direct voter engagement, town halls, and authenticity-driven messaging.'
      };
    }
    
    if (cameraAngle < 0) {
      if (dominantEmotionCategory?.category === 'low_power_emotions') {
        return {
          verdict: 'Strategically aligned',
          color: 'text-cyan-400',
          icon: '→',
          advice: 'Angle reinforces vulnerability messaging—effective for empathy-driven campaigns, but avoid in leadership/authority contexts.'
        };
      } else if (dominantEmotionCategory?.category === 'high_power_emotions') {
        return {
          verdict: 'Strategic misalignment',
          color: 'text-red-400',
          icon: '⚠',
          advice: 'Angle undermines power emotions—this weakens leadership positioning. Re-shoot at eye level or low angle for authority contexts.'
        };
      } else {
        return {
          verdict: 'Transformation effect active',
          color: 'text-yellow-400',
          icon: '⟳',
          advice: 'Angle reinterprets emotion—use intentionally for humble/introspective framing, but test audience response.'
        };
      }
    } else {
      if (dominantEmotionCategory?.category === 'high_power_emotions') {
        return {
          verdict: 'Strategically amplified',
          color: 'text-cyan-400',
          icon: '↑',
          advice: 'Angle maximizes authority presence—ideal for leadership, decisive action, or strength-focused messaging. Risk: may alienate voters seeking relatability.'
        };
      } else if (dominantEmotionCategory?.category === 'low_power_emotions') {
        return {
          verdict: 'Strategic contradiction',
          color: 'text-red-400',
          icon: '⚠',
          advice: 'Angle contradicts vulnerable emotions—creates distrust. Re-shoot from eye level or high angle for authentic empathy messaging.'
        };
      } else {
        return {
          verdict: 'Authority transformation',
          color: 'text-yellow-400',
          icon: '⟳',
          advice: 'Angle shifts toward command/dominance—effective for decisive leadership framing but may reduce approachability.'
        };
      }
    }
  };
  
  const recommendation = getStrategicRecommendation();
  
  const getAngleCategory = (angle) => {
    const abs = Math.abs(angle);
    if (abs < 5) return { label: 'Eye Level', color: 'text-green-400', bg: 'bg-green-900/20', border: 'border-green-800/50' };
    if (angle > 0) {
      if (abs < 15) return { label: 'Low Angle', color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-800/50' };
      return { label: 'Strong Low', color: 'text-blue-500', bg: 'bg-blue-900/30', border: 'border-blue-700/50' };
    } else {
      if (abs < 15) return { label: 'High Angle', color: 'text-purple-400', bg: 'bg-purple-900/20', border: 'border-purple-800/50' };
      return { label: 'Strong High', color: 'text-purple-500', bg: 'bg-purple-900/30', border: 'border-purple-700/50' };
    }
  };
  
  const angleCategory = getAngleCategory(cameraAngle);
  
  return (
    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
      <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
        <Eye size={14} className="mr-2 text-amber-400" /> Viewer Perception Analysis
      </h3>
      
      {/* Strategic Verdict */}
      <div className={`p-3 rounded-lg border mb-4 ${
        recommendation.color.includes('green') ? 'bg-green-900/20 border-green-800/50' :
        recommendation.color.includes('cyan') ? 'bg-cyan-900/20 border-cyan-800/50' :
        recommendation.color.includes('yellow') ? 'bg-yellow-900/20 border-yellow-800/50' :
        'bg-red-900/20 border-red-800/50'
      }`}>
        <div className="flex items-start gap-3 mb-2">
          <span className="text-2xl">{recommendation.icon}</span>
          <div className="flex-1">
            <h4 className={`text-sm font-bold ${recommendation.color} mb-1`}>
              {recommendation.verdict}
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              {recommendation.advice}
            </p>
          </div>
        </div>
      </div>
      
      {/* How Viewers Perceive This Image */}
      <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 p-3 rounded-lg border border-amber-800/30 mb-4">
        <h4 className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-2 flex items-center">
          <UserCheck size={10} className="mr-1.5" /> Unconscious Viewer Response
        </h4>
        <p className="text-xs text-slate-200 leading-relaxed" 
           dangerouslySetInnerHTML={{ __html: generatePerceptionNarrative().replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} 
        />
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={`p-3 rounded-lg border ${angleCategory.bg} ${angleCategory.border}`}>
          <div className="text-center">
            <div className={`text-3xl font-bold font-mono ${angleCategory.color} mb-1`}>
              {cameraAngle > 0 ? '+' : ''}{cameraAngle.toFixed(1)}°
            </div>
            <div className={`text-xs font-bold ${angleCategory.color}`}>
              {angleCategory.label}
            </div>
            <div className="text-[9px] text-slate-500 mt-1">
              {cameraAngle < 0 ? 'Looking Down At Subject' : cameraAngle > 0 ? 'Looking Up At Subject' : 'Direct Eye Contact'}
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-1">Authority Impact</div>
            <div className={`text-2xl font-bold font-mono mb-1 ${
              powerGeo?.confidence_adjusted_multiplier > 1 ? 'text-green-400' : 
              powerGeo?.confidence_adjusted_multiplier < 1 ? 'text-red-400' : 'text-slate-400'
            }`}>
              {powerGeo?.confidence_adjusted_multiplier > 1 ? '+' : ''}
              {Math.round((powerGeo?.confidence_adjusted_multiplier - 1) * 100)}%
            </div>
            <div className="text-[9px] text-slate-500">
              {formatText(powerGeo?.psychological_effect || 'neutral')}
            </div>
          </div>
        </div>
      </div>
      
      {/* Microexpression Angle Impact - NEW SECTION */}
      {microImpacts.length > 0 && (
        <div className="bg-slate-900/30 p-3 rounded-lg border border-slate-700/50 mb-3">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center">
            <Focus size={10} className="mr-1.5 text-pink-500" /> Facial Signal Modifications
          </h4>
          <p className="text-[9px] text-slate-400 mb-3 leading-relaxed">
            This angle {cameraAngle < 0 ? 'diminishes' : 'amplifies'} specific facial micro-expressions, 
            altering how authentic the emotions appear to viewers:
          </p>
          <div className="space-y-2">
            {microImpacts.slice(0, 5).map((impact, i) => (
              <div key={i} className="bg-slate-800/50 p-2 rounded border border-slate-700/30">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-medium text-white">
                    {formatText(impact.name.replace(/_/g, ' ').replace('au', 'AU'))}
                  </span>
                  <span className={`text-xs font-bold font-mono ${
                    impact.change > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {impact.change > 0 ? '+' : ''}{Math.round(impact.change * 100)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${impact.change > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${impact.adjusted * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-[9px] text-slate-500 font-mono w-8 text-right">
                    {Math.round(impact.adjusted * 100)}%
                  </span>
                </div>
                {impact.category && (
                  <div className="mt-1">
                    <span className={`text-[8px] px-1.5 py-0.5 rounded ${
                      impact.category === 'high_power_emotions' ? 'bg-green-900/30 text-green-400' :
                      impact.category === 'low_power_emotions' ? 'bg-red-900/30 text-red-400' :
                      impact.category === 'transformable_emotions' ? 'bg-cyan-900/30 text-cyan-400' :
                      'bg-slate-700/50 text-slate-400'
                    }`}>
                      {formatText(impact.category.replace('_emotions', ''))}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <p className="text-[9px] text-slate-500 italic leading-relaxed">
        Based on Kress & van Leeuwen's visual grammar. Camera angle operates unconsciously—viewers don't notice it, 
        but it shapes their gut response to the subject's authority, trustworthiness, and emotional authenticity.
      </p>
    </div>
  );
};


// ====================================================================================
// SYNTHESIS HELPERS
// ====================================================================================

// === ENVIRONMENTAL CONTEXT SYNTHESIS ===
const generateEnvironmentalContext = (envContext) => {
  if (!envContext) return null;
  
  const formality = envContext.formality_index || 0;
  const sophistication = envContext.environmental_sophistication || 0;
  const affluence = envContext.affluence_score || 0;
  const spaceType = formatText(envContext.community_space_type || "");
  
  let formalityDesc = formality > 0.7 ? "highly formal" : formality > 0.4 ? "semi-formal" : "casual";
  let affluenceDesc = affluence > 0.6 ? "affluent" : affluence > 0.4 ? "middle-class" : "working-class";
  
  return {
    summary: `Captured in a ${formalityDesc} ${spaceType} setting (sophistication: ${Math.round(sophistication * 100)}%), this composition reflects a ${affluenceDesc} community environment.`,
    formality,
    sophistication,
    affluence,
    spaceType
  };
};

// === SOCIAL IDENTITY SYNTHESIS ===
const generateSocialIdentitySynthesis = (socialIdentity) => {
  if (!socialIdentity) return null;
  
  const salience = socialIdentity.ingroup_salience_score || 0;
  const frame = formatText(socialIdentity.identity_frame || "");
  const signals = socialIdentity.ingroup_signals || [];
  
  let intensity = salience > 0.8 ? "Projects powerful" : salience > 0.5 ? "Demonstrates clear" : "Subtly suggests";
  
  const signalTypes = signals.map(s => {
    const parts = s.split('-');
    return parts.length > 1 ? parts[0] : s;
  }).filter((v, i, a) => a.indexOf(v) === i); // unique
  
  return {
    summary: `${intensity} ${frame.toLowerCase()} (${Math.round(salience * 100)}% salience) through ${signalTypes.slice(0, 3).join(', ')} markers, establishing collective belonging and shared identity.`,
    salience,
    frame,
    signals: signalTypes
  };
};

// ====================================================================================
// ENHANCED DANTE SYNTHESIS ENGINE with Natural Variation
// ====================================================================================

// === TONE PHRASE BUILDER ===
const buildTonePhrase = (arousal, valence) => {
  if (arousal > 0.7) {
    return valence > 0.3 ? "energetic optimism" : 
           valence < -0.2 ? "visceral tension" : "heightened intensity";
  } else if (arousal > 0.5) {
    return valence > 0.2 ? "dynamic vitality" : 
           valence < -0.2 ? "urgent gravity" : "active engagement";
  } else if (arousal < 0.3) {
    return valence > 0.2 ? "serene confidence" : 
           valence < -0.2 ? "sombre contemplation" : "quiet restraint";
  } else if (arousal < 0.45) {
    return valence > 0.2 ? "reassuring calm" : "measured seriousness";
  } else {
    return valence > 0.2 ? "warm accessibility" : 
           valence < -0.2 ? "cool detachment" : "balanced composure";
  }
};

// === FRAME PHRASE BUILDER ===
const buildFramePhrase = (angle, distance) => {
  let base = "";
  if (angle.includes("low")) base = "commands authority through an elevated vantage point";
  else if (angle.includes("high")) base = "cultivates empathy via a vulnerable perspective";
  else if (angle.includes("eye") || angle.includes("equal")) base = "establishes parity at eye level";
  else base = "maintains a neutral observational stance";
  
  // Integrate proxemics naturally
  if (distance.includes("close") || distance.includes("intimate")) {
    base = base.replace("through", "through intimate proximity and");
    base = base.replace("via", "via close framing and");
    base = base.replace("at", "through intimate distance at");
  } else if (distance.includes("public") || distance.includes("far")) {
    base = base.replace("through", "through expansive spacing and");
    base = base.replace("via", "via distant framing and");
  }
  
  return base;
};

// === ATMOSPHERE PHRASE BUILDER ===
const buildAtmospherePhrase = (contrast, warmth) => {
  if (contrast > 0.75) return "dramatic high-contrast lighting";
  else if (contrast > 0.6) return "bold chiaroscuro";
  else if (warmth > 0.7) return "inviting warm tones";
  else if (warmth < 0.3) return "clinical cool palette";
  else if (contrast < 0.35) return "soft, even illumination";
  else return "balanced natural lighting";
};

// === RHETORIC DESCRIPTION ===
const getRhetoricDescription = (rhetoric) => {
  const r = rhetoric.toLowerCase();
  if (r === 'pathos') return "emotional resonance";
  if (r === 'logos') return "logical clarity";
  return "institutional credibility";
};

// === TEMPLATES ===
const generateHighEnergyTemplate = (tone, frame, atmosphere, rhetoricDesc, rhetoric, utility, archetype, confidence) => {
  const opener = `Radiating ${tone}, this visual ${frame}.`;
  const middle = confidence > 0.7 
    ? `The ${atmosphere} amplifies the ${formatText(rhetoric)}-driven narrative, establishing ${rhetoricDesc} with precision.`
    : confidence > 0.4
    ? `The ${atmosphere} supports the ${formatText(rhetoric)}-driven approach, building ${rhetoricDesc}.`
    : `The ${atmosphere} attempts to support the ${formatText(rhetoric)} approach, though narrative confidence wavers (${Math.round(confidence * 100)}%), suggesting potential for misinterpretation.`;
  const closer = `Calibrated for ${utility}, it resonates strongly with ${archetype} profiles.`;
  return `${opener} ${middle} ${closer}`;
};

const generateLowEnergyTemplate = (tone, frame, atmosphere, rhetoricDesc, rhetoric, utility, archetype, confidence) => {
  const opener = `Anchored in ${tone}, the composition ${frame}.`;
  const middle = confidence > 0.6
    ? `${atmosphere} reinforces this measured ${formatText(rhetoric)} appeal, grounding the message in ${rhetoricDesc}.`
    : confidence > 0.4
    ? `${atmosphere} attempts to reinforce the ${formatText(rhetoric)} appeal, establishing tentative ${rhetoricDesc}.`
    : `${atmosphere} struggles to establish clear ${formatText(rhetoric)} positioning, with weak narrative structure (${Math.round(confidence * 100)}%) undermining ${rhetoricDesc}.`;
  const closer = archetype !== "general audiences"
    ? `Designed for ${archetype} engagement, it suits ${utility} applications.`
    : `Well-suited to ${utility} contexts.`;
  return `${opener} ${middle} ${closer}`;
};

const generateArchetypeTemplate = (archetype, tone, frame, rhetoricDesc, rhetoric, utility, confidence) => {
  const opener = `Strategically aligned with ${archetype} psychographics, this visual ${frame}.`;
  const middle = confidence > 0.6
    ? `The ${tone} drives ${rhetoricDesc} through a deliberate ${formatText(rhetoric)} framework.`
    : confidence > 0.4
    ? `The ${tone} suggests ${rhetoricDesc}, leveraging ${formatText(rhetoric)}.`
    : `The ${tone} fails to convincingly establish ${rhetoricDesc}, with inconsistent ${formatText(rhetoric)} execution (confidence: ${Math.round(confidence * 100)}%) weakening archetype resonance.`;
  const closer = confidence > 0.5
    ? `Optimised for ${utility}, it delivers targeted psychological impact.`
    : `Intended for ${utility}, but requires refinement to reliably deliver psychological impact.`;
  return `${opener} ${middle} ${closer}`;
};

const generateEmotionalTemplate = (tone, frame, atmosphere, utility, archetype, confidence) => {
  const opener = `This image ${frame}, drawing viewers into ${tone}.`;
  const middle = confidence > 0.5
    ? `The ${atmosphere} intensifies emotional engagement, prioritising visceral response over rational analysis.`
    : `The ${atmosphere} attempts to create emotional engagement, but unclear narrative structure (${Math.round(confidence * 100)}%) dilutes emotional impact and risks viewer disconnection.`;
  const closer = archetype !== "general audiences"
    ? confidence > 0.5
      ? `Particularly effective for ${archetype} audiences in ${utility} contexts.`
      : `May resonate with ${archetype} audiences in ${utility} contexts, but inconsistent execution limits effectiveness.`
    : confidence > 0.5
      ? `Most effective in ${utility} applications requiring immediate emotional impact.`
      : `Intended for ${utility}, but requires stronger narrative clarity to achieve reliable emotional impact.`;
  return `${opener} ${middle} ${closer}`;
};

const generateIdeologicalTemplate = (ideology, tone, frame, utility, archetype, confidence) => {
  const opener = `Framed through a ${ideology} lens, this visual ${frame}.`;
  const middle = confidence > 0.6
    ? `The ${tone} reinforces ideological positioning with clarity and conviction.`
    : confidence > 0.4
    ? `The ${tone} aligns with ${ideology} values, though with subtle ambiguity.`
    : `The ${tone} muddies ideological messaging (confidence: ${Math.round(confidence * 100)}%), creating confusion that may alienate both aligned and opposed audiences.`;
  const closer = confidence > 0.5
    ? `Suited to ${utility} where ${ideology} messaging resonates with ${archetype} audiences.`
    : `Intended for ${utility} with ${archetype} audiences, but ideological ambiguity requires resolution before deployment.`;
  return `${opener} ${middle} ${closer}`;
};

const generateTechnicalTemplate = (atmosphere, frame, tone, rhetoricDesc, rhetoric, utility, confidence) => {
  const opener = `The ${atmosphere} defines this composition, which ${frame}.`;
  const middle = confidence > 0.6
    ? `Combined with ${tone}, it constructs ${rhetoricDesc} through ${formatText(rhetoric)}.`
    : confidence > 0.4
    ? `Combined with ${tone}, it attempts to construct ${rhetoricDesc} through ${formatText(rhetoric)}.`
    : `However, ${tone} fails to coherently deliver ${rhetoricDesc}, with technical elements undermining rather than supporting the ${formatText(rhetoric)} approach (confidence: ${Math.round(confidence * 100)}%).`;
  const closer = confidence > 0.6
    ? `Precisely calibrated for ${utility} with strong technical execution.`
    : confidence > 0.4
    ? `Appropriate for ${utility}, though refinement could enhance impact.`
    : `Unsuitable for ${utility} in current state—technical weaknesses require substantial correction before professional deployment.`;
  return `${opener} ${middle} ${closer}`;
};

const generateBalancedTemplate = (tone, frame, atmosphere, rhetoricDesc, rhetoric, utility, archetype, confidence) => {
  if (confidence < 0.4) {
    return `This visual ${frame}, attempting to project ${tone}. However, the ${atmosphere} fails to coherently support ${formatText(rhetoric)}, resulting in fragmented ${rhetoricDesc} (confidence: ${Math.round(confidence * 100)}%). Substantial revision needed before ${utility} deployment—current execution unlikely to engage ${archetype} audiences effectively.`;
  }
  
  const variation = Math.random();
  const effectivenessClause = confidence > 0.6 ? "effectively" : "adequately, though with room for optimization";
  
  if (variation < 0.33) {
    return `This visual ${frame}, projecting ${tone}. The ${atmosphere} underscores ${formatText(rhetoric)}, establishing ${rhetoricDesc}. Optimised for ${utility}, it engages ${archetype} ${effectivenessClause}.`;
  } else if (variation < 0.66) {
    return `${frame.charAt(0).toUpperCase() + frame.slice(1)}, this composition conveys ${tone} through ${atmosphere}. The ${formatText(rhetoric)}-focused approach builds ${rhetoricDesc}, making it suitable for ${utility} targeting ${archetype} profiles ${confidence > 0.6 ? '' : ', pending refinement'}.`;
  } else {
    return `Designed for ${utility}, this visual leverages ${atmosphere} and ${tone}. By ${frame.replace(/^[a-z]/, c => c.toLowerCase())}, it establishes ${rhetoricDesc} via ${formatText(rhetoric)}, resonating with ${archetype} audiences ${effectivenessClause}.`;
  }
};

// === EMOTIONAL SPECTRUM SYNTHESIS ===
const generateEmotionalSpectrumSynthesis = (selectedImage) => {
  if (!selectedImage) return null;
  
  const valence = selectedImage.avg_valence || 0;
  const arousal = selectedImage.avg_arousal || 0;
  const dominantEmotion = selectedImage.dominant_emotion || "neutral";
  const microexpressions = selectedImage.microexpressions || [];
  
  // Detailed AU mapping with emotional context
  const auEmotionalContext = {
    'AU1': { name: 'inner brow raise', emotion: 'concern', category: 'distress' },
    'AU2': { name: 'outer brow raise', emotion: 'surprise', category: 'activation' },
    'AU4': { name: 'brow furrow', emotion: 'concentration', category: 'focus' },
    'AU5': { name: 'upper lid raise', emotion: 'alertness', category: 'activation' },
    'AU6': { name: 'cheek raise', emotion: 'genuine pleasure', category: 'joy' },
    'AU7': { name: 'lid tightening', emotion: 'intensity', category: 'focus' },
    'AU9': { name: 'nose wrinkle', emotion: 'disgust', category: 'rejection' },
    'AU10': { name: 'upper lip raise', emotion: 'contempt', category: 'rejection' },
    'AU12': { name: 'lip corner pull', emotion: 'happiness', category: 'joy' },
    'AU14': { name: 'dimpler', emotion: 'warmth', category: 'joy' },
    'AU15': { name: 'lip corner depress', emotion: 'sadness', category: 'distress' },
    'AU17': { name: 'chin raise', emotion: 'defiance', category: 'dominance' },
    'AU18': { name: 'lip pucker', emotion: 'consideration', category: 'focus' },
    'AU20': { name: 'lip stretch', emotion: 'fear', category: 'distress' },
    'AU23': { name: 'lip tightening', emotion: 'suppression', category: 'control' },
    'AU24': { name: 'lip press', emotion: 'restraint', category: 'control' },
    'AU25': { name: 'lips part', emotion: 'openness', category: 'receptivity' },
    'AU26': { name: 'jaw drop', emotion: 'shock', category: 'activation' },
    'AU27': { name: 'mouth stretch', emotion: 'terror', category: 'distress' },
    'AU43': { name: 'eyes closed', emotion: 'serenity', category: 'peace' },
    'AU45': { name: 'blink', emotion: 'processing', category: 'neutral' },
  };
  
  // Parse microexpressions and categorize
  const parsedAUs = microexpressions.map(au => {
    const codes = au.includes('+') ? au.split('+').map(c => c.trim()) : [au];
    return codes.map(code => auEmotionalContext[code]).filter(Boolean);
  }).flat();
  
  const categories = [...new Set(parsedAUs.map(au => au.category))];
  const hasJoy = categories.includes('joy');
  const hasDistress = categories.includes('distress');
  const hasFocus = categories.includes('focus');
  const hasControl = categories.includes('control');
  const hasDominance = categories.includes('dominance');
  const hasActivation = categories.includes('activation');
  
  // Determine emotional quadrant and intensity
  const isHighArousal = arousal > 0.6;
  const isLowArousal = arousal < 0.35;
  const isPositive = valence > 0.2;
  const isNegative = valence < -0.2;
  const isIntense = arousal > 0.7 || Math.abs(valence) > 0.5;
  
  // Template selection based on patterns
  
  // TEMPLATE 1: Authentic Joy (AU6 + AU12 with positive valence)
  if (hasJoy && isPositive && parsedAUs.some(au => au.emotion === 'genuine pleasure')) {
    const joySignals = parsedAUs.filter(au => au.category === 'joy').map(au => au.name);
    return `Displaying authentic ${dominantEmotion.toLowerCase()} through ${joySignals.join(' and ')}, this visual achieves ${valence > 0.5 ? 'powerful' : 'moderate'} positive resonance (valence: +${valence.toFixed(2)}). The ${isHighArousal ? 'energetic' : 'serene'} presentation (arousal: ${Math.round(arousal * 100)}%) signals genuine emotional engagement, building trust through involuntary micro-expressions that viewers unconsciously recognize as authentic.`;
  }
  
  // TEMPLATE 2: Controlled Intensity (High arousal + control signals)
  if (hasControl && isHighArousal) {
    const controlSignals = parsedAUs.filter(au => au.category === 'control').map(au => au.name);
    return `Projecting controlled ${dominantEmotion.toLowerCase()} with high arousal (${Math.round(arousal * 100)}%), the composition reveals ${controlSignals.join(' and ')}—signals of emotional regulation and deliberate restraint. ${isPositive ? 'Despite positive valence' : 'Combined with negative valence'} (+${valence.toFixed(2)}), these micro-expressions suggest measured intensity, creating gravitas and signaling that the emotion is managed rather than spontaneous.`;
  }
  
  // TEMPLATE 3: Distress/Concern (Distress category)
  if (hasDistress && isNegative) {
    const distressSignals = parsedAUs.filter(au => au.category === 'distress').map(au => au.name).slice(0, 2);
    const intensity = Math.abs(valence) > 0.5 ? 'profound' : 'notable';
    return `Exhibiting ${intensity} ${dominantEmotion.toLowerCase()} through ${distressSignals.join(' and ')}, this image carries ${intensity} negative affect (valence: ${valence.toFixed(2)}) with ${isHighArousal ? 'heightened' : 'subdued'} arousal (${Math.round(arousal * 100)}%). These involuntary facial cues signal genuine emotional distress, potentially triggering empathetic responses or cautionary interpretation depending on context.`;
  }
  
  // TEMPLATE 4: Alert Focus (Focus + activation)
  if ((hasFocus || hasActivation) && isHighArousal) {
    const focusSignals = parsedAUs.filter(au => au.category === 'focus' || au.category === 'activation').map(au => au.name).slice(0, 2);
    return `Demonstrating intense ${dominantEmotion.toLowerCase()} with ${focusSignals.join(' and ')}, the visual conveys high cognitive engagement (arousal: ${Math.round(arousal * 100)}%). ${isPositive ? 'Coupled with positive affect' : 'Despite neutral affect'} (valence: ${valence > 0 ? '+' : ''}${valence.toFixed(2)}), these micro-expressions signal active processing and heightened attention, suggesting the subject is deeply engaged with their environment or task.`;
  }
  
  // TEMPLATE 5: Dominance/Defiance
  if (hasDominance) {
    const dominanceSignals = parsedAUs.filter(au => au.category === 'dominance').map(au => au.name);
    return `Expressing ${dominantEmotion.toLowerCase()} through ${dominanceSignals.join(' and ')}, this composition projects authority and resistance with ${isHighArousal ? 'forceful' : 'measured'} energy (arousal: ${Math.round(arousal * 100)}%). The ${isPositive ? 'positive' : 'negative'} emotional tone (valence: ${valence > 0 ? '+' : ''}${valence.toFixed(2)}) combined with dominance signals creates ${isPositive ? 'confident assertiveness' : 'defiant opposition'}, establishing clear power dynamics.`;
  }
  
  // TEMPLATE 6: Mixed Signals (Contradictory AUs)
  if (hasJoy && hasDistress) {
    return `Displaying complex ${dominantEmotion.toLowerCase()} with contradictory micro-expressions—combining markers of both pleasure and distress—this visual reveals emotional ambivalence. The ${isPositive ? 'positive' : 'negative'} valence (${valence > 0 ? '+' : ''}${valence.toFixed(2)}) at ${Math.round(arousal * 100)}% arousal suggests nuanced emotional states like bittersweet sentiment, nervous excitement, or conflicted feelings that invite deeper viewer interpretation.`;
  }
  
  // TEMPLATE 7: High Arousal, Positive (Excitement/Energy)
  if (isHighArousal && isPositive && parsedAUs.length > 0) {
    const topSignals = parsedAUs.slice(0, 2).map(au => au.name);
    return `Radiating energetic ${dominantEmotion.toLowerCase()} through ${topSignals.join(' and ')}, the image achieves powerful emotional impact with high arousal (${Math.round(arousal * 100)}%) and strong positive valence (+${valence.toFixed(2)}). These micro-expressions amplify the sense of excitement and vitality, creating contagious emotional energy that drives engagement and captures attention.`;
  }
  
  // TEMPLATE 8: Low Arousal, Positive (Calm/Contentment)
  if (isLowArousal && isPositive && parsedAUs.length > 0) {
    const topSignals = parsedAUs.slice(0, 2).map(au => au.name);
    return `Conveying tranquil ${dominantEmotion.toLowerCase()} with ${topSignals.join(' and ')}, this composition maintains calm positive affect (valence: +${valence.toFixed(2)}) at low arousal (${Math.round(arousal * 100)}%). The subtle micro-expressions signal contentment and serenity rather than excitement, creating reassuring emotional stability that builds comfort and trust.`;
  }
  
  // TEMPLATE 9: Low Arousal, Negative (Melancholy/Resignation)
  if (isLowArousal && isNegative && parsedAUs.length > 0) {
    const topSignals = parsedAUs.slice(0, 2).map(au => au.name);
    return `Expressing subdued ${dominantEmotion.toLowerCase()} through ${topSignals.join(' and ')}, the visual carries quiet negative affect (valence: ${valence.toFixed(2)}) with minimal arousal (${Math.round(arousal * 100)}%). These restrained micro-expressions suggest melancholy, resignation, or contemplative sadness—emotions that evoke empathy through their understated authenticity.`;
  }
  
  // TEMPLATE 10: Neutral/Ambiguous (Default fallback)
  if (parsedAUs.length > 0) {
    const topSignals = parsedAUs.slice(0, 2).map(au => au.name);
    return `Presenting ${dominantEmotion.toLowerCase()} with ${topSignals.join(' and ')}, this image maintains ${Math.abs(valence) < 0.2 ? 'emotionally neutral' : valence > 0 ? 'mildly positive' : 'mildly negative'} affect (valence: ${valence > 0 ? '+' : ''}${valence.toFixed(2)}) at ${arousal > 0.5 ? 'moderate' : 'low'} arousal (${Math.round(arousal * 100)}%). The subtle micro-expressions provide emotional nuance without overwhelming the composition, allowing for flexible viewer interpretation.`;
  }
  
  // FALLBACK: No microexpressions detected
  return `Projecting ${dominantEmotion.toLowerCase()} with ${valence > 0.2 ? 'positive' : valence < -0.2 ? 'negative' : 'neutral'} affect (valence: ${valence > 0 ? '+' : ''}${valence.toFixed(2)}) and ${arousal > 0.6 ? 'high' : arousal > 0.4 ? 'moderate' : 'low'} arousal (${Math.round(arousal * 100)}%), this visual establishes clear emotional positioning. The absence of detectable micro-expressions suggests ${arousal < 0.3 ? 'calm restraint' : 'controlled presentation'}, creating a clean emotional anchor for viewer interpretation.`;
};

// === CRITICAL QUALITY TEMPLATE ===
const generateLowQualityTemplate = (technicalQuality, compositionScore, narrativeScore, coherenceScore, tone, atmosphere, overallQuality) => {
  const issues = [];
  if (technicalQuality < 0.4) issues.push("poor technical execution");
  if (compositionScore < 0.4) issues.push("weak compositional structure");
  if (narrativeScore < 0.4) issues.push("unclear narrative");
  if (coherenceScore < 0.4) issues.push("message incoherence");
  
  const mainIssue = issues[0] || "suboptimal visual quality";
  
  return `This visual struggles with ${mainIssue}, undermining its persuasive potential. The ${atmosphere} fails to compensate for structural weaknesses, while the ${tone} lacks conviction (quality score: ${Math.round(overallQuality * 100)}%). Substantial refinement required before deployment—current state risks damaging credibility and reducing engagement effectiveness.`;
};

// === MODERATE QUALITY TEMPLATE ===
const generateMediumQualityTemplate = (overallQuality, tone, frame, atmosphere, rhetoricDesc, rhetoric, utility, archetype) => {
  return `Demonstrating workable but improvable execution (quality: ${Math.round(overallQuality * 100)}%), this visual ${frame} with ${tone}. The ${atmosphere} provides adequate support for ${formatText(rhetoric)}, establishing baseline ${rhetoricDesc}. While functional for ${utility}, the composition shows room for optimization—particularly in narrative clarity and technical polish—to maximize impact with ${archetype} audiences.`;
};

// === NEGATIVE EMOTIONAL IMPACT TEMPLATE ===
const generateNegativeEmotionalTemplate = (valence, arousal, dominantEmotion, frame, atmosphere, utility) => {
  const intensity = Math.abs(valence) > 0.5 ? "strongly" : "moderately";
  const energyDesc = arousal > 0.6 ? "agitated" : arousal < 0.3 ? "subdued" : "unsettled";
  
  return `Projecting ${intensity} negative affect (valence: ${valence.toFixed(2)}) with ${energyDesc} energy (arousal: ${Math.round(arousal * 100)}%), this visual ${frame}. The ${atmosphere} amplifies the ${dominantEmotion.toLowerCase()} tone, potentially triggering avoidance responses rather than engagement. Exercise caution deploying for ${utility}—negative emotional framing may backfire except in specific contrast or urgency-driven contexts.`;
};

// === CONFUSING/INCOHERENT TEMPLATE ===
const generateIncoherentTemplate = (coherenceScore, tone, frame, utility) => {
  return `This composition suffers from message fragmentation (coherence: ${Math.round(coherenceScore * 100)}%), with visual and symbolic elements failing to reinforce a unified narrative. The ${tone} clashes with the framing approach, while ${frame.toLowerCase()}. Recommended for revision before ${utility} deployment—current incoherence risks audience confusion and diminished trust.`;
};

// === MAIN GENERATOR FUNCTION ===
const generateDanteSynthesis = (image, semiotics) => {
  if (!image) return "Data insufficient for synthesis.";

  const arousal = image.avg_arousal || 0;
  const valence = image.avg_valence || 0;
  const angle = (semiotics.powerAngle || "").toLowerCase();
  const distance = (semiotics.socialDistance || "").toLowerCase();
  const contrast = image.contrast_level || 0.5;
  const warmth = image.color_warmth_index || 0.5;
  const confidence = image.narrative_structure?.narrative_confidence || 0.5;
  
  // NEW: Quality assessment
  const technicalQuality = image.technical_quality_score || 0;
  const compositionScore = image.composition_score || 0;
  const narrativeScore = image.narrative_structure?.narrative_score || 0;
  const coherenceScore = image.multimodal_coherence?.multimodal_coherence_score || 0;
  
  // Calculate overall quality
  const overallQuality = (technicalQuality + compositionScore + narrativeScore + coherenceScore) / 4;
  const isLowQuality = overallQuality < 0.4;
  const isMediumQuality = overallQuality >= 0.4 && overallQuality < 0.65;
  
  const archetypeEntry = image.archetype_compatibility_scores?.most_resonant 
      ? Object.entries(image.archetype_compatibility_scores.most_resonant).sort((a,b) => b[1] - a[1])[0] 
      : null;
  const archetype = archetypeEntry ? formatText(archetypeEntry[0]) : "general audiences";
  const archetypeScore = archetypeEntry ? archetypeEntry[1] : 0;
  
  const utility = formatText(image.visual_complexity?.optimal_for || 
                 image.visual_rhetoric?.communicative_function || 
                 "general messaging");
  
  const rhetoric = image.visual_rhetoric?.rhetorical_appeals?.[0] || "Ethos";
  const ideologyFrame = formatText(image.dominant_ideology || image.ideological_frame || "Neutral");
  
  const tonePhrase = buildTonePhrase(arousal, valence);
  const framePhrase = buildFramePhrase(angle, distance);
  const atmospherePhrase = buildAtmospherePhrase(contrast, warmth);
  const rhetoricDesc = getRhetoricDescription(rhetoric);
  
  const signals = {
    emotional: arousal > 0.65 || arousal < 0.35 || Math.abs(valence) > 0.4,
    archetype: archetypeScore > 0.7,
    rhetorical: rhetoric.toLowerCase() === 'pathos' || rhetoric.toLowerCase() === 'logos',
    ideological: ideologyFrame !== "Neutral" && ideologyFrame !== "Mixed",
    technical: contrast > 0.7 || warmth > 0.7 || warmth < 0.3
  };
  
  // PRIORITY 1: Critical quality issues
  if (isLowQuality) {
    return generateLowQualityTemplate(technicalQuality, compositionScore, narrativeScore, coherenceScore, tonePhrase, atmospherePhrase, overallQuality);
  }
  
  // PRIORITY 2: Message incoherence
  if (coherenceScore < 0.45 && coherenceScore > 0) {
    return generateIncoherentTemplate(coherenceScore, tonePhrase, framePhrase, utility);
  }
  
  // PRIORITY 3: Negative emotional impact (when not intentionally rhetorical)
  if (valence < -0.3 && !signals.rhetorical) {
    return generateNegativeEmotionalTemplate(valence, arousal, image.dominant_emotion || "negative", framePhrase, atmospherePhrase, utility);
  }
  
  // PRIORITY 4: Medium quality
  if (isMediumQuality) {
    return generateMediumQualityTemplate(overallQuality, tonePhrase, framePhrase, atmospherePhrase, rhetoricDesc, rhetoric, utility, archetype);
  }
  
  // PRIORITY 5: Existing high-quality templates
  if (signals.emotional && arousal > 0.65) {
    return generateHighEnergyTemplate(tonePhrase, framePhrase, atmospherePhrase, rhetoricDesc, rhetoric, utility, archetype, confidence);
  } else if (signals.emotional && arousal < 0.35) {
    return generateLowEnergyTemplate(tonePhrase, framePhrase, atmospherePhrase, rhetoricDesc, rhetoric, utility, archetype, confidence);
  } else if (signals.archetype) {
    return generateArchetypeTemplate(archetype, tonePhrase, framePhrase, rhetoricDesc, rhetoric, utility, confidence);
  } else if (signals.rhetorical && rhetoric.toLowerCase() === 'pathos') {
    return generateEmotionalTemplate(tonePhrase, framePhrase, atmospherePhrase, utility, archetype, confidence);
  } else if (signals.ideological) {
    return generateIdeologicalTemplate(ideologyFrame, tonePhrase, framePhrase, utility, archetype, confidence);
  } else if (signals.technical) {
    return generateTechnicalTemplate(atmospherePhrase, framePhrase, tonePhrase, rhetoricDesc, rhetoric, utility, confidence);
  } else {
    return generateBalancedTemplate(tonePhrase, framePhrase, atmospherePhrase, rhetoricDesc, rhetoric, utility, archetype, confidence);
  }
};

// === SAFE SEMIOTICS PARSER ===
const getSemiotics = (selectedImage) => {
    if (!selectedImage) return {};

    // Priority 1: Direct root
    let data = selectedImage.visual_semiotics;
    
    // Priority 2: Inside candidate_details
    if (!data && selectedImage.candidate_details?.visual_semiotics) {
        data = selectedImage.candidate_details.visual_semiotics;
    }

    // Priority 3: Inside visual_rhetoric
    if (!data && selectedImage.visual_rhetoric?.visual_semiotics) {
        data = selectedImage.visual_rhetoric.visual_semiotics;
    }

    // Priority 4: Inside general analysis object
    if (!data && selectedImage.analysis?.visual_semiotics) {
        data = selectedImage.analysis.visual_semiotics;
    }
    
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch (e) {
            console.error("Failed to parse visual_semiotics string", e);
            return {};
        }
    }
    
    if (!data || typeof data !== 'object') return {};

    const getVal = (key1, key2) => data[key1] || data[key2] || "N/A";

    return {
        imageAct: getVal('image_act', 'imageAct'),
        socialDistance: getVal('social_distance', 'socialDistance'),
        powerAngle: getVal('power_angle', 'powerAngle'),
        involvementAngle: getVal('involvement_angle', 'involvementAngle')
    };
};


// ====================================================================================
// ImageUploadDialog
// ====================================================================================

const ImageUploadDialog = ({ campaignId, isOpen, onClose }) => {
  if (!isOpen) return null;
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragEnter = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  
  const handleDrop = (e) => {
    e.preventDefault(); setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      files.forEach((file) => uploadBytesToStorage(`campaigns/${campaignId}/images/${file.name}`,file));
      onClose();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      files.forEach((file) => uploadBytesToStorage(`campaigns/${campaignId}/images/${file.name}`,file));
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 p-8 rounded-xl w-full max-w-lg shadow-[0_0_15px_rgba(255,255,255,0.4)] border border-slate-700 animate-fade-in-up-subtle">
        <h2 className="text-2xl font-bold font-grotesk text-white mb-6 flex items-center">
          <UploadCloud className="mr-3 text-cyan-400" size={24} /> Upload Campaign Images
        </h2>
        <div className={`flex flex-col items-center justify-center p-12 border-2 border-dashed rounded-lg transition-colors duration-200 ${isDragOver ? 'border-cyan-500 bg-slate-700/50' : 'border-slate-600 bg-slate-900/50 hover:border-slate-500'}`} onDragOver={handleDragEnter} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          <ImageIcon size={40} className="text-slate-400 mb-3" />
          <p className="text-slate-300 font-semibold mb-1">Drag & Drop your images here</p>
          <p className="text-sm text-slate-500 mb-4">PNG, JPG, SVG, up to 10MB per file</p>
          <label htmlFor="file-upload" className="cursor-pointer bg-cyan-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-cyan-500 transition-colors duration-200 font-medium">Browse Files</label>
          <input id="file-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================================
// ImageGallery (MAIN COMPONENT)
// ====================================================================================

function ImageGallery({ campaignId, onDavinciClick }) {
  const [images, setImages] = useState([]);
  const [imageURLs, setImageURLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('synopsis');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [copiedColor, setCopiedColor] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      if (!campaignId) { setError('No campaign ID provided'); setLoading(false); return; }
      try {
        setLoading(true); setError(null);
        const user = auth.currentUser;
        if (!user) throw new Error("No user signed in.");
        const token = await user.getIdToken();
        const response = await fetch(`https://us-central1-knoesis-dante.cloudfunctions.net/getCampaignImages?campaignID=${campaignId}`, {
          method: "GET", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setImages(data || []);
      } catch (err) {
        console.error('Error fetching images:', err);
        setError(`Failed to load images: ${err.message}`);
        setImages([]);
      } finally { setLoading(false); }
    };
    fetchImages();
  }, [campaignId]);

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await fetch(`https://us-central1-knoesis-dante.cloudfunctions.net/deleteCampaignImage?campaignID=${campaignId}&imgID=${imageId}`, {
          method: "GET", headers: {"Content-Type": "application/json" },
        });
       setImages(prev => prev.filter(img => img.id !== imageId));
      setSelectedImage(null);
    } catch (err) { alert(`Failed to delete image: ${err.message}`); }
  };

  const getPath = (url) => decodeURIComponent(url.split("//")[1].split("/")[5].split("?")[0]);

  useEffect(() => {
    Promise.allSettled(images.map((image) => getStorageURL(getPath(image.displayable_url))))
      .then((urls) => setImageURLs(urls.map((url) => url.value)));
  }, [images]);

  // MOVED USEMEMO TO TOP LEVEL
  const danteSummary = useMemo(() => {
    if (!selectedImage) return "";
    const semiotics = getSemiotics(selectedImage);
    return generateDanteSynthesis(selectedImage, semiotics);
  }, [selectedImage]);

  const goToNextImage = () => {
    if (!selectedImage) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  };

  const goToPrevImage = () => {
    if (!selectedImage) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIndex]);
  };

  const handleCopyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopiedColor(hex);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-grotesk font-bold text-white mb-2">Image Gallery</h1>
          <p className="text-slate-400 text-sm">
            {images.length} {images.length === 1 ? 'image' : 'images'} in collection
          </p>
        </div>
        
        {!loading && images.length > 0 && (
          <div className="flex gap-3">
             {/* Image Strategy Button */}
            <button
            onClick={onDavinciClick}
               className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 hover:border-slate-500 rounded-lg transition-colors duration-200 font-semibold text-sm shadow-sm"
            >
              <Sparkles size={16} className="text-white-400"/> Image Strategy
            </button>

            <button 
              onClick={() => setIsUploadOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors duration-200 font-semibold text-sm shadow-md"
            >
              <Plus size={16} /> Add Images
            </button>
          </div>
        )}
      </header>

      {error && (
        <div className="max-w-7xl mx-auto mb-6 bg-red-900/50 border border-red-700 text-red-300 p-4 rounded-lg flex items-start gap-3">
          <AlertTriangle size={20} className="mt-0.5" />
          <div><h3 className="font-bold">Error Loading Gallery</h3><p className="text-sm text-red-400">{error}</p></div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <Loader2 size={48} className="animate-spin mb-4" />
          <p className="text-lg font-grotesk">Loading gallery...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <button onClick={() => setIsUploadOpen(true)} className="relative aspect-square overflow-hidden rounded-lg bg-slate-800 border-2 border-slate-700 hover:border-white transition-all duration-200 group flex flex-col items-center justify-center text-slate-500 hover:text-cyan-400">
            <Plus size={48} className="mb-2" /><span className="text-sm font-grotesk font-semibold">Upload your first image</span>
          </button>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <button
              key={image.id}
              onClick={() => setSelectedImage(image)}
              className="relative aspect-square overflow-hidden rounded-lg bg-slate-800 border border-slate-700 hover:border-white transition-all duration-200 group hover:shadow-[0_0_15px_rgba(255,255,255,0.4)]"
            >
              <img
                src={imageURLs[index]}
                alt={`Gallery image ${image.id}`}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                onError={(e) => { e.target.src = 'https://placehold.co/400x400/1e293b/64748b?text=Error+Loading'; }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
            </button>
          ))}
        </div>
      )}

      {/* Expanded Image Modal */}
      {selectedImage && (() => {
        const selectedIndex = images.findIndex(img => img.id === selectedImage.id);
        const signedDisplayUrl = imageURLs[selectedIndex] || 'https://placehold.co/800x600/1e293b/64748b?text=Loading...';
        
        const getTabClass = (id) => `
          flex-1 py-3 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-colors duration-200
          ${activeTab === id 
            ? 'border-white text-white bg-slate-800' 
            : 'border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}
        `;

        const topArchetypeEntry = selectedImage.archetype_compatibility_scores?.most_resonant 
            ? Object.entries(selectedImage.archetype_compatibility_scores.most_resonant).sort((a,b) => b[1] - a[1])[0] 
            : null;

        const semiotics = getSemiotics(selectedImage);

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSelectedImage(null)}>
            <div className="relative w-full max-w-6xl h-[90vh] flex flex-col lg:flex-row gap-4 animate-fade-in-up-subtle shadow-[0_0_50px_rgba(0,0,0,0.5)]" onClick={(e) => e.stopPropagation()}>
              
              {/* Left: Image Viewer */}
              <div className="flex-1 flex items-center justify-center relative bg-black/80 rounded-xl overflow-hidden border border-slate-700">
                <img
                  src={signedDisplayUrl}
                  alt={`Expanded view ${selectedImage.id}`}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.src = 'https://placehold.co/800x600/1e293b/64748b?text=Image+Not+Found'; }}
                />
                <button onClick={goToPrevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white/50 hover:text-white hover:bg-black/80 rounded-full transition-all duration-200 backdrop-blur-md border border-white/10"><ChevronLeft size={24} /></button>
                <button onClick={goToNextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white/50 hover:text-white hover:bg-black/80 rounded-full transition-all duration-200 backdrop-blur-md border border-white/10"><ChevronRight size={24} /></button>
              </div>

              {/* Right: Data Sidebar */}
              <div className="lg:w-[400px] bg-slate-900 rounded-xl border border-slate-700 overflow-hidden flex flex-col max-h-[90vh] shadow-xl">
                
                <div className="p-4 border-b border-slate-700 bg-slate-900 z-10">
                  <div className="flex justify-between items-start mb-4">
                     <div>
                       <h2 className="text-lg font-grotesk font-bold text-white leading-tight">DANTE | Vision</h2>
                       <p className="text-xs text-slate-500 mt-1 font-mono">{selectedImage.id.slice(0, 8)}...</p>
                     </div>
                     <button onClick={() => setSelectedImage(null)} className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
                  </div>
                  <div className="flex border-b border-slate-700">
                    <button onClick={() => setActiveTab('synopsis')} className={getTabClass('synopsis')}>Synopsis</button>
                    <button onClick={() => setActiveTab('psych')} className={getTabClass('psych')}>Psych</button>
                    <button onClick={() => setActiveTab('targeting')} className={getTabClass('targeting')}>Targeting</button>
                    <button onClick={() => setActiveTab('tech')} className={getTabClass('tech')}>Tech</button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar bg-slate-900">
                  
                  {/* --- TAB: SYNOPSIS --- */}
                  {activeTab === 'synopsis' && (
                    <div className="space-y-4 animate-fade-in-up-subtle">
                      
                      {/* Ad Performance Prediction Banner */}
                      {selectedImage.visual_complexity?.ad_performance_prediction && (
                        <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 p-4 rounded-lg border-2 border-cyan-500/50 mb-4 shadow-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="bg-cyan-500/20 p-2 rounded-lg">
                                <TrendingUp size={20} className="text-cyan-400" />
                              </div>
                              <div>
                                <span className="text-[9px] text-cyan-400 uppercase tracking-widest block mb-1">Performance Forecast</span>
                                <span className="text-base font-bold text-white">
                                  {formatText(selectedImage.visual_complexity.ad_performance_prediction)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xs text-slate-400 block">Predicted Impact</span>
                              <span className="text-lg font-bold text-cyan-400">
                                {Math.round((selectedImage.visual_complexity.visual_complexity_score || 0) * 100)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                       {/* DANTE INSIGHTS */}
                      <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 rounded-lg border border-slate-700/50 shadow-sm relative overflow-hidden group">
                        <div className="relative z-10">
                            <h3 className="text-[10px] font-bold text-indigo-300 uppercase tracking-widest mb-2 flex items-center">
                                <Sparkles size={10} className="mr-1.5 text-indigo-400" /> Dante Insights
                            </h3>
                            <p className="text-sm text-slate-200 leading-relaxed font-sans font-medium">
                                "{danteSummary}"
                            </p>
                        </div>
                      </div>

                      {/* 1. IMPACT CORE HUD */}
                      <div className="grid grid-cols-3 gap-2">
                        {/* Impact Score */}
                        <div className="bg-gradient-to-br from-cyan-900/40 to-slate-900 border border-cyan-800/50 p-2 py-3 rounded-lg flex flex-col items-center justify-center text-center relative overflow-hidden">
                           <div className="absolute top-0 right-0 p-1">
                               {selectedImage.narrative_structure?.narrative_confidence > 0.6 ? 
                                 <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.8)]" title="High Confidence"/> : 
                                 <AlertCircle size={10} className="text-amber-500" title="Review Suggested"/>}
                           </div>
                           <TrendingUp size={16} className="text-cyan-400 mb-1" />
                           <span className="text-xl font-bold font-mono text-white">
                             {Math.round((selectedImage.narrative_structure?.narrative_score || 0) * 100)}
                           </span>
                           <span className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Emotional Impact</span>
                           <span className="text-[9px] font-bold text-cyan-200 leading-tight px-1 truncate w-full">
                               {formatText(selectedImage.dominant_emotion || "Neutral")}
                           </span>
                        </div>

                        {/* Visual Load */}
                        <div className="bg-slate-800/50 border border-slate-700/50 p-2 py-3 rounded-lg flex flex-col items-center justify-center text-center">
                           <BrainCircuit size={16} className="text-purple-400 mb-1" />
                           <span className="text-xl font-bold font-mono text-white">
                             {Math.round((selectedImage.visual_complexity?.visual_complexity_score || 0) * 100)}
                           </span>
                           <span className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Visual Load</span>
                           <span className="text-[9px] font-bold text-purple-200 leading-tight px-1 truncate w-full">
                               {formatText(selectedImage.visual_complexity?.optimal_for || "General")}
                           </span>
                        </div>

                         {/* Trust */}
                        <div className="bg-slate-800/50 border border-slate-700/50 p-2 py-3 rounded-lg flex flex-col items-center justify-center text-center">
                           <Shield size={16} className="text-green-400 mb-1" />
                           <span className="text-xs font-bold text-white mt-1 mb-0.5 line-clamp-1">
                             {formatText(selectedImage.narrative_structure?.reliability_tier || "N/A")}
                           </span>
                           <span className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Trust</span>
                           <span className="text-[9px] font-bold text-green-200 leading-tight px-1 truncate w-full">
                               Confidence: {Math.round((selectedImage.narrative_structure?.narrative_confidence || 0) * 100)}%
                           </span>
                        </div>
                      </div>

                      {/* Symbolic Themes */}
                      {selectedImage.symbolic_themes?.length > 0 && (
                        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 p-4 rounded-lg border border-purple-700/50">
                          <h3 className="text-xs font-bold text-purple-300 uppercase tracking-wider mb-3 flex items-center">
                            <Sparkles size={14} className="mr-2 text-purple-400" /> Strategic Pillars
                          </h3>
                          <div className="grid grid-cols-3 gap-2">
                            {selectedImage.symbolic_themes.slice(0, 3).map((theme, i) => {
                              const icons = {
                                competence: <Target size={16} className="text-blue-400" />,
                                action: <Zap size={16} className="text-yellow-400" />,
                                patriotism: <Shield size={16} className="text-red-400" />,
                                trust: <Lock size={16} className="text-green-400" />,
                                innovation: <Lightbulb size={16} className="text-cyan-400" />,
                                authority: <Scale size={16} className="text-purple-400" />,
                              };
                              const icon = icons[theme.toLowerCase()] || <Sparkles size={16} className="text-slate-400" />;
                              
                              return (
                                <div key={i} className="bg-slate-900/50 p-3 rounded border border-purple-800/30 text-center">
                                  <div className="flex justify-center mb-1">{icon}</div>
                                  <span className="text-xs font-bold text-white capitalize">{theme}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Cognitive Frame */}
                      {selectedImage.lakoff_image_schemas?.length > 0 && (
                        <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 flex items-center">
                            <Brain size={12} className="mr-2 text-indigo-400" /> Cognitive Frame
                          </h3>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedImage.lakoff_image_schemas.map((schema, i) => (
                              <span key={i} className="text-[10px] bg-indigo-900/30 text-indigo-300 px-2 py-1 rounded border border-indigo-800/50 font-medium">
                                {formatText(schema)}
                              </span>
                            ))}
                          </div>
                          <p className="text-[9px] text-slate-500 mt-2 italic">
                            Unconscious metaphorical structures that shape viewer interpretation
                          </p>
                        </div>
                      )}

                    

                      {/* Message Integrity */}
                      {selectedImage.multimodal_coherence && (
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <CheckCircle size={14} className="mr-2 text-cyan-400" /> Message Integrity
                          </h3>
                          
                          <div className="mb-3">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-slate-400">Coherence Score</span>
                              <span className={`text-sm font-bold font-mono ${
                                selectedImage.multimodal_coherence.multimodal_coherence_score > 0.7 ? 'text-green-400' : 
                                selectedImage.multimodal_coherence.multimodal_coherence_score > 0.5 ? 'text-yellow-400' : 
                                'text-red-400'
                              }`}>
                                {Math.round((selectedImage.multimodal_coherence.multimodal_coherence_score || 0) * 100)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${
                                  selectedImage.multimodal_coherence.multimodal_coherence_score > 0.7 ? 'bg-green-500' : 
                                  selectedImage.multimodal_coherence.multimodal_coherence_score > 0.5 ? 'bg-yellow-500' : 
                                  'bg-red-500'
                                }`}
                                style={{ width: `${(selectedImage.multimodal_coherence.multimodal_coherence_score || 0) * 100}%` }}
                              />
                            </div>
                          </div>
                          
                          {selectedImage.multimodal_coherence.coherence_checks_passed?.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {selectedImage.multimodal_coherence.coherence_checks_passed.map((check, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                  {check.includes('mismatch') ? (
                                    <AlertCircle size={12} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                                  ) : (
                                    <CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                                  )}
                                  <span className={check.includes('mismatch') ? 'text-yellow-400' : 'text-green-400'}>
                                    {formatText(check)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="bg-slate-900/50 p-2 rounded">
                            <span className="text-[9px] text-slate-500 block mb-1">Clarity Level</span>
                            <span className="text-xs font-bold text-white">
                              {formatText(selectedImage.multimodal_coherence.message_clarity || "N/A")}
                            </span>
                          </div>
                          
                          {selectedImage.multimodal_coherence.recommendation && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                              <span className="text-[9px] text-slate-500 block mb-1">Recommendation:</span>
                              <span className="text-xs text-cyan-300 font-medium">
                                {formatText(selectedImage.multimodal_coherence.recommendation)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Environmental Context */}
                      {selectedImage.environmental_context && (() => {
                        const envData = generateEnvironmentalContext(selectedImage.environmental_context);
                        if (!envData) return null;
                        
                        return (
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                              <Compass size={14} className="mr-2 text-emerald-400" /> Setting Context
                            </h3>
                            
                            <p className="text-xs text-slate-300 leading-relaxed mb-3 italic">
                              "{envData.summary}"
                            </p>
                            
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              <div className="bg-slate-900/50 p-2 rounded text-center">
                                <span className="text-[9px] text-slate-500 block mb-1">Formality</span>
                                <span className="text-sm font-bold text-white">{Math.round(envData.formality * 100)}%</span>
                              </div>
                              <div className="bg-slate-900/50 p-2 rounded text-center">
                                <span className="text-[9px] text-slate-500 block mb-1">Sophistication</span>
                                <span className="text-sm font-bold text-white">{Math.round(envData.sophistication * 100)}%</span>
                              </div>
                              <div className="bg-slate-900/50 p-2 rounded text-center">
                                <span className="text-[9px] text-slate-500 block mb-1">Affluence</span>
                                <span className="text-sm font-bold text-white">{Math.round(envData.affluence * 100)}%</span>
                              </div>
                            </div>
                            
                            {selectedImage.environmental_context.institutional_contexts?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <span className="text-[9px] text-slate-500 block mb-2">Institutional Context:</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {selectedImage.environmental_context.institutional_contexts.map((ctx, i) => (
                                    <span key={i} className="text-[10px] bg-emerald-900/30 text-emerald-300 px-2 py-0.5 rounded border border-emerald-800/50">
                                      {formatText(ctx)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* 2. THE STORY VECTOR */}
                    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 relative overflow-hidden">
                        <div className="absolute -right-4 -top-4 w-24 h-24 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-full blur-xl pointer-events-none"></div>
                        
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <BookOpen size={14} className="mr-2 text-indigo-400" /> Story Vector
                        </h3>
                        
                        <div className="space-y-3 relative z-10">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs text-slate-500">Rhetorical Frame</span>
                                <p className="text-sm text-white leading-relaxed">
                                  This visual employs <span className="font-bold text-indigo-300">{formatText(selectedImage.visual_rhetoric?.dominant_rhetoric || selectedImage.ideological_frame || "Neutral")}</span> rhetoric, 
                                  functioning as <span className="font-bold text-indigo-300">{formatText(selectedImage.visual_rhetoric?.communicative_function || "Information").toLowerCase()}</span> communication.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-700/30">
                                <div className="flex items-center gap-2 bg-slate-900/30 p-2 rounded">
                                    <Zap size={12} className="text-amber-400"/>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-slate-500 uppercase">Energy</span>
                                        <span className="text-xs font-medium text-slate-200">
                                            {formatText(selectedImage.action_dynamics?.energy_level || "Static")}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-slate-900/30 p-2 rounded">
                                    <Activity size={12} className="text-blue-400"/>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-slate-500 uppercase">Focus</span>
                                        <span className="text-xs font-medium text-slate-200">
                                            {formatText(selectedImage.action_dynamics?.temporal_frame || "Present")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                      {/* Narrative Arc Intelligence */}
                      {selectedImage.narrative_structure && (
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <BookOpen size={14} className="mr-2 text-amber-400" /> Story Potential
                          </h3>
                          
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className={`p-3 rounded border ${selectedImage.narrative_structure.has_agency ? 'bg-green-900/20 border-green-800/50' : 'bg-slate-900/50 border-slate-700/50'}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Agency</span>
                                {selectedImage.narrative_structure.has_agency ? 
                                  <CheckCircle size={14} className="text-green-400"/> : 
                                  <X size={14} className="text-slate-600"/>}
                              </div>
                            </div>
                            <div className={`p-3 rounded border ${selectedImage.narrative_structure.has_conflict ? 'bg-green-900/20 border-green-800/50' : 'bg-slate-900/50 border-slate-700/50'}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Conflict</span>
                                {selectedImage.narrative_structure.has_conflict ? 
                                  <CheckCircle size={14} className="text-green-400"/> : 
                                  <X size={14} className="text-slate-600"/>}
                              </div>
                            </div>
                            <div className={`p-3 rounded border ${selectedImage.narrative_structure.has_problem ? 'bg-green-900/20 border-green-800/50' : 'bg-slate-900/50 border-slate-700/50'}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Problem</span>
                                {selectedImage.narrative_structure.has_problem ? 
                                  <CheckCircle size={14} className="text-green-400"/> : 
                                  <X size={14} className="text-slate-600"/>}
                              </div>
                            </div>
                            <div className={`p-3 rounded border ${selectedImage.narrative_structure.has_solution ? 'bg-green-900/20 border-green-800/50' : 'bg-slate-900/50 border-slate-700/50'}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Solution</span>
                                {selectedImage.narrative_structure.has_solution ? 
                                  <CheckCircle size={14} className="text-green-400"/> : 
                                  <X size={14} className="text-slate-600"/>}
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-slate-900/50 p-2 rounded">
                            <span className="text-[9px] text-slate-500 block mb-1">Interpretation</span>
                            <span className="text-xs text-slate-200 font-medium">
                              {selectedImage.narrative_structure.interpretation_recommendation && 
                               selectedImage.narrative_structure.interpretation_recommendation.trim() !== "" 
                                ? formatText(selectedImage.narrative_structure.interpretation_recommendation) 
                                : "Narrative Score: " + Math.round((selectedImage.narrative_structure.narrative_score || 0) * 100) + "%"}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* 3. COMMUNICATION CHANNELS (STACKED) */}
                      <div className="flex flex-col gap-4">
                          
                          {/* Top: Non-Verbal (Semiotics) - UPDATED WITH SAFE PARSER */}
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 flex flex-col h-full">
                               <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                  <Eye size={14} className="mr-2 text-teal-400" /> Advanced Semiotics
                              </h3>
                              
                              <div className="space-y-3 flex-1">
                                  {/* Map directly from the safe 'semiotics' object defined above */}
                                  <div className="grid grid-cols-1 gap-3">
                                      {/* Image Act */}
                                      <div className="flex flex-col">
                                          <span className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Image Act</span>
                                          <div className="flex items-center gap-2 text-sm text-teal-200 font-bold bg-slate-900/50 p-1.5 rounded border border-slate-700/50">
                                              <Focus size={12} className="text-teal-500"/>
                                              {formatText(semiotics.imageAct)}
                                          </div>
                                      </div>

                                      {/* Social Distance */}
                                      <div className="flex flex-col">
                                          <span className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Social Distance</span>
                                          <div className="flex items-center gap-2 text-sm text-white font-medium bg-slate-900/50 p-1.5 rounded border border-slate-700/50">
                                              <Users size={12} className="text-blue-400"/>
                                              {formatText(semiotics.socialDistance)}
                                          </div>
                                      </div>

                                      <div className="grid grid-cols-2 gap-2 mt-1">
                                          {/* Power Angle */}
                                          <div>
                                              <span className="text-[9px] text-slate-500 block mb-0.5">Power Angle</span>
                                              <span className="text-xs text-slate-300 font-medium bg-slate-900/50 px-1.5 py-1 rounded block truncate" title={formatText(semiotics.powerAngle)}>
                                                  {formatText(semiotics.powerAngle)}
                                              </span>
                                          </div>
                                          {/* Involvement Angle */}
                                          <div>
                                              <span className="text-[9px] text-slate-500 block mb-0.5">Involvement</span>
                                              <span className="text-xs text-slate-300 font-medium bg-slate-900/50 px-1.5 py-1 rounded block truncate" title={formatText(semiotics.involvementAngle)}>
                                                  {formatText(semiotics.involvementAngle)}
                                              </span>
                                          </div>
                                      </div>
                                  </div>
                                  
                                  {/* Body Language Chips */}
                                  {selectedImage.body_language_signals?.length > 0 && (
                                      <div className="pt-2 border-t border-slate-700/30 mt-2">
                                          <span className="text-[9px] text-slate-500 block mb-1">Signals:</span>
                                          <div className="flex flex-wrap gap-1.5">
                                              {selectedImage.body_language_signals.slice(0, 3).map((signal, i) => (
                                                  <span key={i} className="text-[9px] text-teal-200 bg-teal-900/30 px-1.5 py-0.5 rounded border border-teal-800/30">
                                                      {formatText(signal)}
                                                  </span>
                                              ))}
                                          </div>
                                      </div>
                                  )}

                                  {/* Spatial Power Dynamics */}
                                  {selectedImage.spatial_relationships && (
                                    <div className="mt-3 pt-3 border-t border-slate-700/30">
                                      <span className="text-[10px] text-slate-500 uppercase tracking-wide mb-2 block">Spatial Dynamics</span>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-slate-900/50 p-2 rounded">
                                          <span className="text-[9px] text-slate-500 block mb-1">Formation</span>
                                          <span className="text-xs text-white font-medium">
                                            {formatText(selectedImage.spatial_relationships.interaction_formation || "N/A")}
                                          </span>
                                        </div>
                                        <div className="bg-slate-900/50 p-2 rounded">
                                          <span className="text-[9px] text-slate-500 block mb-1">Power Geometry</span>
                                          <span className="text-xs text-white font-medium">
                                            {formatText(selectedImage.spatial_relationships.power_geometry || "N/A")}
                                          </span>
                                        </div>
                                      </div>
                                      {selectedImage.spatial_relationships.spatial_equity_score !== undefined && (
                                        <div className="mt-2 flex justify-between items-center text-xs">
                                          <span className="text-slate-500">Equity Score:</span>
                                          <span className="font-mono text-teal-300">
                                            {selectedImage.spatial_relationships.spatial_equity_score.toFixed(2)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                          </div>

                          {/* Bottom: Verbal (Text & Voice) */}
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 flex flex-col h-full">
                               <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                  <Mic2 size={14} className="mr-2 text-pink-400" /> Voice
                              </h3>
                              
                              {(selectedImage.text_sentiment_analysis || selectedImage.typography_personality) ? (
                                  <div className="space-y-3 flex-1">
                                      {/* Sentiment Badge */}
                                      {selectedImage.text_sentiment_analysis && (
                                          <div className={`p-2 rounded border flex items-center justify-center ${
                                                selectedImage.text_sentiment_analysis.sentiment_label === 'positive' ? 'bg-green-900/20 border-green-800/50 text-green-300' :
                                                selectedImage.text_sentiment_analysis.sentiment_label === 'negative' ? 'bg-red-900/20 border-red-800/50 text-red-300' :
                                                'bg-slate-900/30 border-slate-700 text-slate-400'
                                          }`}>
                                              <span className="text-xs font-bold uppercase tracking-wide">
                                                  {selectedImage.text_sentiment_analysis.sentiment_label}
                                              </span>
                                          </div>
                                      )}

                                      {/* Typography Personality */}
                                      {selectedImage.typography_personality?.length > 0 ? (
                                          <div className="flex flex-wrap gap-1.5 content-start">
                                              {selectedImage.typography_personality.slice(0, 4).map((trait, i) => (
                                                  <span key={i} className="text-[10px] text-slate-300 bg-slate-700/50 px-1.5 py-0.5 rounded">
                                                      {formatText(trait)}
                                                  </span>
                                              ))}
                                          </div>
                                      ) : <span className="text-[10px] text-slate-500 italic">No text traits detected</span>}
                                  </div>
                              ) : (
                                  <div className="flex-1 flex items-center justify-center text-xs text-slate-500 italic">
                                      No textual elements
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* 4. DEEP STRUCTURE (Pentad) */}
                      {selectedImage.visual_rhetoric?.burkes_pentad && (
                        <PentadDisplay pentad={selectedImage.visual_rhetoric.burkes_pentad} />
                      )}
                      
                    </div>
                  )}

                  {/* --- TAB: PSYCH --- */}
                  {activeTab === 'psych' && (
                    <div className="space-y-6 animate-fade-in-up-subtle">
                        
                        {/* Resonance Profile */}
                        <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-5 rounded-xl border border-indigo-500/30 relative overflow-hidden shadow-lg">
                           <div className="absolute top-0 right-0 p-3 opacity-20">
                             <Brain size={48} className="text-white"/>
                           </div>
                           <h3 className="text-xs font-bold text-indigo-300 uppercase tracking-widest mb-3">Resonance Profile</h3>
                           
                           <div className="space-y-3 relative z-10">
                              <div>
                                 <span className="text-[10px] text-indigo-200 uppercase tracking-wide opacity-70">Primary Archetype</span>
                                 <div className="text-xl font-grotesk font-bold text-white flex items-center gap-2">
                                     {topArchetypeEntry ? formatText(topArchetypeEntry[0]) : "Undetermined"}
                                     {topArchetypeEntry && (
                                        <span className="text-xs font-mono bg-indigo-500/30 px-1.5 py-0.5 rounded text-indigo-200">
                                           {Math.round(topArchetypeEntry[1] * 100)}%
                                        </span>
                                     )}
                                 </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-indigo-500/30">
                                  <div>
                                      <span className="text-[10px] text-indigo-200 uppercase tracking-wide opacity-70">Ideology</span>
                                      <div className="text-sm font-bold text-white leading-tight mt-0.5">
                                         {formatText(selectedImage.dominant_ideology || "Neutral")}
                                      </div>
                                  </div>
                                  <div>
                                      <span className="text-[10px] text-indigo-200 uppercase tracking-wide opacity-70">Tone</span>
                                      <div className="text-sm font-bold text-white leading-tight mt-0.5">
                                         {formatText(selectedImage.dominant_emotion || "Neutral")}
                                      </div>
                                  </div>
                              </div>
                           </div>
                        </div>

                        {/* Social Identity Dynamics */}
                        {selectedImage.social_identity_compatibility && (() => {
                          const identityData = generateSocialIdentitySynthesis(selectedImage.social_identity_compatibility);
                          if (!identityData) return null;
                          
                          return (
                            <div className="bg-gradient-to-br from-blue-900/30 to-slate-900 p-4 rounded-lg border border-blue-700/50">
                              <h3 className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-3 flex items-center">
                                <Users size={14} className="mr-2 text-blue-400" /> Group Identity Signal
                              </h3>
                              
                              <p className="text-xs text-slate-200 leading-relaxed mb-3 italic">
                                "{identityData.summary}"
                              </p>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-slate-900/50 p-2 rounded">
                                  <span className="text-[9px] text-slate-500 block mb-1">Identity Frame</span>
                                  <span className="text-xs font-bold text-white">{identityData.frame}</span>
                                </div>
                                <div className="bg-slate-900/50 p-2 rounded">
                                  <span className="text-[9px] text-slate-500 block mb-1">Salience</span>
                                  <span className={`text-xs font-bold ${identityData.salience > 0.8 ? 'text-blue-400' : 'text-slate-300'}`}>
                                    {Math.round(identityData.salience * 100)}%
                                  </span>
                                </div>
                              </div>
                              
                              {selectedImage.social_identity_compatibility.spatial_equity_score !== undefined && (
                                <div className="mt-3 pt-3 border-t border-blue-800/30">
                                  <div className="flex justify-between items-center">
                                    <span className="text-[9px] text-slate-500">Spatial Equity Score:</span>
                                    <span className="text-xs font-mono text-slate-300">
                                      {selectedImage.social_identity_compatibility.spatial_equity_score.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Psychographic Drivers */}
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                                <Compass size={14} className="mr-2 text-pink-500" /> Psychographic Drivers
                            </h3>
                            <div className="space-y-3">
                                {selectedImage.threat_sensitivity_compatibility && (
                                    <div className="flex justify-between items-center border-b border-slate-700/50 pb-2">
                                        <span className="text-sm text-slate-400">Threat Sensitivity</span>
                                        <div className="text-right">
                                            <span className="block text-sm font-bold text-white">
                                                {formatText(selectedImage.threat_sensitivity_compatibility.dominant_frame || "Neutral")}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {selectedImage.institutional_trust_compatibility && (
                                    <div className="flex justify-between items-center border-b border-slate-700/50 pb-2">
                                        <span className="text-sm text-slate-400">Institutional Trust</span>
                                        <div className="text-right">
                                            <span className="block text-sm font-bold text-white">
                                                 {formatText(selectedImage.institutional_trust_compatibility.institutional_positioning || "Neutral")}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                 {selectedImage.openness_compatibility && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-400">Openness Appeal</span>
                                        <div className="text-right">
                                            <span className="block text-sm font-bold text-white">
                                                 {formatText(selectedImage.openness_compatibility.visual_style || "Neutral")}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Values & Time */}
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Activity size={14} className="mr-2 text-blue-400" /> Values & Time
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                                    <span className="text-xs text-slate-500 block mb-1">Materialism</span>
                                    <span className="text-sm text-white font-semibold">
                                        {formatText(selectedImage.materialism_compatibility?.dominant_orientation || "Mixed")}
                                    </span>
                                </div>
                                <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                                    <span className="text-xs text-slate-500 block mb-1">Time Horizon</span>
                                    <span className="text-sm text-white font-semibold">
                                        {formatText(selectedImage.temporal_compatibility?.psychological_temporal_frame || "Present Focused")}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Message Complexity */}
                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                             <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Message Complexity</span>
                             <span className={`text-xs font-bold px-2 py-1 rounded border ${
                                 selectedImage.conceptual_complexity_compatibility?.message_style === 'high_complexity' 
                                 ? 'bg-purple-900/30 text-purple-300 border-purple-800' 
                                 : 'bg-blue-900/30 text-blue-300 border-blue-800'
                             }`}>
                                 {formatText(selectedImage.conceptual_complexity_compatibility?.message_style || "Standard")}
                             </span>
                        </div>

                        {/* Emotional Spectrum */}
<div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
        <Heart size={14} className="mr-2 text-red-500" /> Emotional Spectrum
    </h3>
    
    {/* Synthesis Summary */}
    <div className="bg-gradient-to-r from-red-900/20 to-pink-900/20 p-3 rounded-lg border border-red-800/30 mb-4">
        <p className="text-xs text-slate-200 leading-relaxed italic">
            "{generateEmotionalSpectrumSynthesis(selectedImage)}"
        </p>
    </div>
    
    {/* Metrics */}
    <PolarityMeter label="Valence (Pleasure)" value={selectedImage.avg_valence} />
    <ScoreBar label="Arousal (Energy)" value={selectedImage.avg_arousal} colorClass="bg-orange-500" />
    
    <div className="mt-4 pt-3 border-t border-slate-700/50">
        <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Dominant Emotion</span>
            <span className="text-sm font-bold text-white uppercase">{selectedImage.dominant_emotion}</span>
        </div>
    </div>
</div>

{/* Power Geometry - NEW SECTION */}
<PowerGeometryAnalysis selectedImage={selectedImage} />

{/* AI Confidence */}
<div className={`p-4 rounded-lg border flex flex-col gap-2 ${
    selectedImage.psychographic_confidence?.confidence_tier === 'high' ? 'bg-green-950/30 border-green-900/50' :
    selectedImage.psychographic_confidence?.confidence_tier === 'moderate' ? 'bg-yellow-950/30 border-yellow-900/50' :
    'bg-slate-900 border-slate-800'
}`}>
                            <div className="flex justify-between items-center">
                                <h3 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2 text-slate-400">
                                    <Lock size={12} className={selectedImage.psychographic_confidence?.confidence_tier === 'high' ? 'text-green-600' : 'text-yellow-600'}/>
                                    DANTE Vision Confidence
                                </h3>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                    selectedImage.psychographic_confidence?.confidence_tier === 'high' ? 'bg-green-900/20 text-green-500' : 'bg-yellow-900/20 text-yellow-500'
                                }`}>
                                    {selectedImage.psychographic_confidence?.confidence_tier === 'high' ? 'HIGH RELIABILITY' : 'MODERATE'}
                                </span>
                            </div>
                            
                            {/* Contextual Adjustments */}
                            {selectedImage.contextual_adjustments && Object.keys(selectedImage.contextual_adjustments).length > 0 && (
                                <div className="mt-1 text-xs text-slate-500 border-t border-white/5 pt-2">
                                    <div className="italic opacity-70">
                                        "{formatText(Object.values(selectedImage.contextual_adjustments)[0] || "Standard analysis applied.")}"
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                  )}

                  {/* --- TAB: TARGETING --- */}
                  {activeTab === 'targeting' && (
                    <div className="space-y-6 animate-fade-in-up-subtle">
                         
                         {/* Resonant Archetypes */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                                <Target size={14} className="mr-2 text-red-400" /> Resonant Archetypes
                            </h3>
                            {selectedImage.archetype_compatibility_scores?.most_resonant ? (
                                <div className="space-y-3">
                                    {Object.entries(selectedImage.archetype_compatibility_scores.most_resonant)
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 3)
                                        .map(([name, score]) => (
                                        <div key={name} className="relative">
                                            <div className="flex justify-between text-xs mb-1 z-10 relative">
                                                <span className="font-bold text-white">{formatText(name)}</span>
                                                <span className="text-cyan-400 font-mono">{Math.round(score * 100)}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                                                <div className="h-full bg-cyan-600" style={{ width: `${score * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <span className="text-sm text-slate-500">No archetype data available</span>}
                         </div>

                        {/* Brand Personality Fit (NEW) */}
                        {selectedImage.brand_personality_dimensions?.length > 0 && (
                          <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 p-4 rounded-lg border border-indigo-700/50">
                            <h3 className="text-xs font-bold text-indigo-300 uppercase tracking-wider mb-3 flex items-center">
                              <Sparkles size={14} className="mr-2 text-indigo-400" /> Brand Personality Match
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {selectedImage.brand_personality_dimensions.map((trait, i) => (
                                <div key={i} className="px-3 py-2 bg-indigo-900/30 text-indigo-200 text-xs rounded-lg border border-indigo-800/50 font-bold">
                                  {formatText(trait)}
                                </div>
                              ))}
                            </div>
                            <p className="text-[9px] text-slate-500 mt-3 italic">
                              Personality dimensions this visual projects for brand alignment
                            </p>
                          </div>
                        )}

                         {/* Ideological Spectrum */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center">
                                <Shield size={14} className="mr-2 text-green-500" /> Ideological Spectrum
                            </h3>
                            
                            <ScoreBar label="Conservative (RWA)" value={selectedImage.rwa_raw_score} max={1} colorClass="bg-blue-600" />
                            <ScoreBar label="Social Dominance (SDO)" value={selectedImage.sdo_raw_score} max={1} colorClass="bg-red-600" />
                            <ScoreBar label="Progressive Signals" value={selectedImage.progressive_raw_score} max={1} colorClass="bg-green-600" />
                            <ScoreBar label="Centrist/Moderate" value={selectedImage.centrist_raw_score} max={1} colorClass="bg-yellow-500" />
                            <ScoreBar label="Populist Intensity" value={selectedImage.populist_raw_score} max={1} colorClass="bg-orange-600" />
                            
                            <div className="mt-3 p-2 bg-slate-900/50 rounded text-center">
                                <span className="text-xs text-slate-500 uppercase tracking-widest block mb-1">Primary Frame</span>
                                <span className="text-sm font-bold text-white uppercase">{formatText(selectedImage.dominant_ideology || "Mixed")}</span>
                            </div>
                         </div>
                         
                         {/* Political Signifiers */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                             <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Fingerprint size={14} className="mr-2 text-purple-400" /> Political Signifiers
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {selectedImage.political_symbols?.detected_political_symbols?.length > 0 ? (
                                    selectedImage.political_symbols.detected_political_symbols.map((sym, i) => (
                                        <Tag key={i}>{formatText(sym.split(':')[0])}</Tag>
                                    ))
                                ) : <span className="text-xs text-slate-500 italic">No overt symbols detected</span>}
                            </div>
                            
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700/30">
                                <span className="text-xs text-slate-400">Neutrality Check:</span>
                                {selectedImage.political_symbols?.is_party_neutral ? (
                                    <span className="text-xs font-bold text-green-400">Neutral / Ambiguous</span>
                                ) : (
                                    <span className="text-xs font-bold text-orange-400">Partisan Signals Detected</span>
                                )}
                            </div>
                         </div>

                         {/* Persuasion Strategy */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                             <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Scale size={14} className="mr-2 text-amber-400" /> Persuasion Strategy
                            </h3>
                            <div className="flex gap-2">
                                {['ethos', 'pathos', 'logos'].map((mode) => {
                                    const isActive = selectedImage.visual_rhetoric?.rhetorical_appeals?.includes(mode);
                                    return (
                                        <div key={mode} className={`flex-1 p-2 rounded text-center border ${
                                            isActive 
                                            ? 'bg-slate-700 border-slate-500 text-white' 
                                            : 'bg-slate-900/30 border-slate-800 text-slate-600'
                                        }`}>
                                            <span className="text-xs font-bold uppercase block">{mode}</span>
                                            <span className="text-[10px] opacity-70">
                                                {mode === 'ethos' ? 'Credibility' : mode === 'pathos' ? 'Emotion' : 'Logic'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                         </div>

                        {/* Emotional Targeting Matrix */}
                        {selectedImage.emotion_appraisal_reasoning && (
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                              <Brain size={14} className="mr-2 text-pink-400" /> Emotional Targeting Logic
                            </h3>
                            <div className="bg-slate-900/50 p-3 rounded border border-slate-700/50">
                              <p className="text-xs text-slate-300 leading-relaxed">
                                {(() => {
                                  // Parse the reasoning string into readable English
                                  const segments = selectedImage.emotion_appraisal_reasoning.split('|').map(s => s.trim());
                                  const transitions = segments.map(seg => {
                                    // Extract emotion → target (context: X) pattern
                                    const match = seg.match(/'([^']+)'\s*→\s*'([^']+)'\s*\(context:\s*([^)]+)\)/);
                                    if (match) {
                                      const [, fromEmotion, toEmotion, context] = match;
                                      return `${formatText(fromEmotion)} transitions to ${formatText(toEmotion)} in ${formatText(context)} settings`;
                                    }
                                    return seg.replace(/'/g, '');
                                  });
                                  
                                  return transitions.join(', while ') + '.';
                                })()}
                              </p>
                            </div>
                            {selectedImage.emotion_reappraisal?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <span className="text-[9px] text-slate-500 uppercase tracking-wider block mb-2">Target Emotional Frames</span>
                                <div className="flex flex-wrap gap-2">
                                  {selectedImage.emotion_reappraisal.map((emotion, i) => (
                                    <span key={i} className="px-2 py-1 bg-pink-900/30 text-pink-300 text-xs rounded border border-pink-800/50 font-medium">
                                      {formatText(emotion)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* FACS Micro-Expression Analysis */}
                        {selectedImage.facs_analysis && (
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                              <Eye size={14} className="mr-2 text-amber-400" /> FACS Micro-Analysis
                            </h3>
                            <div className="space-y-2">
                              {selectedImage.facs_analysis.fear && (
                                <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded border border-slate-800">
                                  <span className="text-xs text-slate-400">Fear Indicators</span>
                                  <span className="text-xs text-yellow-400 font-mono">
                                    Brow: {Math.round((selectedImage.facs_analysis.fear.brow_raise_score || 0) * 100)}% | 
                                    Eye: {Math.round((selectedImage.facs_analysis.fear.eye_widen_score || 0) * 100)}%
                                  </span>
                                </div>
                              )}
                              {selectedImage.facs_analysis.skepticism && (
                                <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded border border-slate-800">
                                  <span className="text-xs text-slate-400">Skepticism Signal</span>
                                  <span className="text-xs text-purple-400 font-mono">
                                    Tension: {Math.round((selectedImage.facs_analysis.skepticism.brow_tension_score || 0) * 100)}%
                                  </span>
                                </div>
                              )}
                            </div>
                             <p className="text-[9px] text-slate-500 mt-2 italic">
                               *Facial Action Coding System (FACS) detects involuntary micro-expressions indicating subconscious sentiment.
                             </p>
                          </div>
                        )}

                        {/* Behavioral Activation Profile (NEW) */}
                        {selectedImage.action_dynamics && (
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                              <Zap size={14} className="mr-2 text-orange-400" /> Behavioral Activation
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-slate-900/50 p-2 rounded">
                                <span className="text-[9px] text-slate-500 block mb-1">Energy Profile</span>
                                <span className="text-sm font-bold text-white">
                                  {formatText(selectedImage.action_dynamics.energy_level)}
                                </span>
                                <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500"
                                    style={{ width: `${(selectedImage.action_dynamics.energy_score || 0) * 100}%` }}
                                  />
                                </div>
                              </div>
                              <div className="bg-slate-900/50 p-2 rounded">
                                <span className="text-[9px] text-slate-500 block mb-1">Change Orientation</span>
                                <span className="text-xs text-white font-medium">
                                  {formatText(selectedImage.action_dynamics.change_stability_orientation)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Temporal Context Targeting (NEW) */}
                        {selectedImage.contextual_timing && (
                          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                              <Activity size={14} className="mr-2 text-blue-400" /> Temporal Context
                            </h3>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {selectedImage.contextual_timing.inferred_season && (
                                <div className="bg-slate-900/50 p-2 rounded">
                                  <span className="text-slate-500 block mb-1">Season</span>
                                  <span className="text-white font-medium">{formatText(selectedImage.contextual_timing.inferred_season)}</span>
                                </div>
                              )}
                              {selectedImage.contextual_timing.inferred_time_of_day && (
                                <div className="bg-slate-900/50 p-2 rounded">
                                  <span className="text-slate-500 block mb-1">Time of Day</span>
                                  <span className="text-white font-medium">{formatText(selectedImage.contextual_timing.inferred_time_of_day)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                         {/* Strategic Utility */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                             <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Lightbulb size={14} className="mr-2 text-yellow-400" /> Strategic Utility
                            </h3>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <span className="text-xs text-slate-500 block mb-1">Recommendation</span>
                                    <div className="p-2 bg-slate-900/50 rounded border border-slate-700/30 text-white font-medium">
                                        {formatText(selectedImage.multimodal_coherence?.recommendation || selectedImage.institutional_trust_compatibility?.use_recommendation || "N/A")}
                                    </div>
                                </div>
                                 <div>
                                    <span className="text-xs text-slate-500 block mb-1">Optimal Application</span>
                                    <div className="p-2 bg-slate-900/50 rounded border border-slate-700/30 text-cyan-300 font-medium">
                                        {formatText(selectedImage.visual_complexity?.optimal_for || "General Use")}
                                    </div>
                                </div>
                            </div>
                         </div>

                         {/* Audience & Inclusivity */}
                         <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                             <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Users size={14} className="mr-2 text-teal-500" /> Demographics & Inclusivity
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div>
                                    <span className="text-xs text-slate-500 block">Audience Type</span>
                                    <span className="text-sm text-white font-medium">{formatText(selectedImage.audience_type || "General")}</span>
                                </div>
                                 <div>
                                    <span className="text-xs text-slate-500 block">Inclusivity Score</span>
                                    <span className={`text-sm font-bold ${selectedImage.accessibility_representation?.inclusivity_score > 0.5 ? 'text-green-400' : 'text-yellow-400'}`}>
                                        {Math.round((selectedImage.accessibility_representation?.inclusivity_score || 0) * 100)}%
                                    </span>
                                </div>
                            </div>

                            {/* Enhanced Demographic Inference */}
                            {selectedImage.demographic_inference && (
                                <div className="mb-3 pt-3 border-t border-slate-700/30 grid grid-cols-2 gap-3">
                                    <div>
                                        <span className="text-xs text-slate-500 block">Age Context</span>
                                        <span className="text-sm text-slate-200">{formatText(selectedImage.demographic_inference.likely_age_context || "Varied")}</span>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-500 block">Lifestyle</span>
                                        <span className="text-sm text-slate-200">
                                            {selectedImage.demographic_inference.lifestyle_indicators?.[0] ? formatText(selectedImage.demographic_inference.lifestyle_indicators[0]) : "General"}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {selectedImage.accessibility_representation?.representation_gaps?.length > 0 && (
                                <div className="mt-2">
                                    <span className="text-xs text-slate-500 block mb-1">Representation Gaps:</span>
                                    <ul className="list-disc list-inside text-xs text-slate-300 space-y-1">
                                        {selectedImage.accessibility_representation.representation_gaps.map((gap, i) => (
                                            <li key={i}>{gap}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                         </div>
                    </div>
                  )}

                  {/* --- TAB: TECH --- */}
                  {activeTab === 'tech' && (
                    <div className="space-y-6 animate-fade-in-up-subtle">
                        
                        {/* TECH HUD SUMMARY */}
                        <div className="grid grid-cols-3 gap-2">
                           {/* Tech Score */}
                           <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700 flex flex-col items-center justify-center text-center">
                              <Aperture size={16} className="text-cyan-400 mb-1" />
                              <span className="text-2xl font-bold font-mono text-white">
                                {selectedImage.technical_quality_score?.toFixed(1) || 0}
                              </span>
                              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Quality</span>
                           </div>
                           
                           {/* Comp Score */}
                           <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700 flex flex-col items-center justify-center text-center">
                              <Maximize size={16} className="text-purple-400 mb-1" />
                              <span className="text-2xl font-bold font-mono text-white">
                                {selectedImage.composition_score?.toFixed(1) || 0}
                              </span>
                              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Composition</span>
                           </div>

                           {/* Focus Score */}
                           <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700 flex flex-col items-center justify-center text-center">
                              <Sliders size={16} className="text-green-400 mb-1" />
                              <span className="text-2xl font-bold font-mono text-white">
                                {Math.round((selectedImage.inferred_focus_quality || 0) * 10)}
                              </span>
                              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Focus</span>
                           </div>
                        </div>

                        {/* Optimal Application */}
                        <div className="p-2 bg-slate-800/30 rounded border border-dashed border-slate-700 text-center">
                             <span className="text-xs text-slate-400">Optimal Application: </span>
                             <span className="text-xs font-bold text-cyan-200">
                                 {formatText(selectedImage.visual_complexity?.optimal_for || "General Application")}
                             </span>
                        </div>

                       {/* Gestalt Salience */}
                      {selectedImage.gestalt_salience_score !== undefined && (
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <Grid size={14} className="mr-2 text-indigo-400" /> Gestalt Salience
                          </h3>
                          
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-slate-400">Perceptual Grouping Quality</span>
                            <span className={`text-2xl font-bold font-mono ${
                              selectedImage.gestalt_salience_score > 0.7 ? 'text-green-400' : 
                              selectedImage.gestalt_salience_score > 0.5 ? 'text-yellow-400' : 
                              'text-red-400'
                            }`}>
                              {Math.round(selectedImage.gestalt_salience_score * 100)}
                            </span>
                          </div>
                          
                          <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden mb-3">
                            <div 
                              className={`h-full transition-all duration-500 ${
                                selectedImage.gestalt_salience_score > 0.7 ? 'bg-green-500' : 
                                selectedImage.gestalt_salience_score > 0.5 ? 'bg-yellow-500' : 
                                'bg-red-500'
                              }`}
                              style={{ width: `${selectedImage.gestalt_salience_score * 100}%` }}
                            />
                          </div>
                          
                          <p className="text-[10px] text-slate-500 italic leading-relaxed">
                            Measures how effectively the composition uses visual grouping principles (proximity, similarity, continuity, closure). 
                            Higher scores indicate stronger perceptual organization and easier cognitive processing.
                          </p>
                        </div>
                      )}

                        {/* Camera Physics */}
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                             <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                                <Sun size={14} className="mr-2 text-yellow-500" /> Camera Physics
                            </h3>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <ScoreBar label="Lighting Quality" value={selectedImage.lighting_quality} colorClass="bg-yellow-400" />
                                  <ScoreBar label="Contrast Level" value={selectedImage.contrast_level} colorClass="bg-gray-200" />
                                </div>
                                <div className="flex justify-between items-center text-sm pt-2 border-t border-slate-700/30">
                                    <div className="flex flex-col">
                                       <span className="text-xs text-slate-500">Focus Type</span>
                                       <span className="font-medium text-white">{formatText(selectedImage.focus_type_prediction || "Standard")}</span>
                                    </div>
                                    <div className="flex flex-col text-right">
                                       <span className="text-xs text-slate-500">Exposure</span>
                                       <span className="font-medium text-white">{formatText(selectedImage.exposure_evaluation || "Balanced")}</span>
                                    </div>
                                </div>
                                {/* Depth Cues */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-400">Depth Perception:</span>
                                    {selectedImage.has_depth_cues ? (
                                        <span className="text-xs font-bold text-green-400 flex items-center"><CheckCircle size={10} className="mr-1"/> Detected</span>
                                    ) : (
                                        <span className="text-xs text-slate-600">Flat Field</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Geometry & Balance (UPDATED COMPONENT) */}
                        <GeometryAndBalance 
                            selectedImage={selectedImage}
                            signedDisplayUrl={signedDisplayUrl} 
                            formatText={formatText} 
                        />

                        

                        {/* Color Psychology */}
                      {(selectedImage.color_arousal !== undefined || selectedImage.color_dominance !== undefined || selectedImage.color_pleasure !== undefined) && (
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <Palette size={14} className="mr-2 text-pink-400" /> Color Psychology
                          </h3>
                          
                          <div className="space-y-3">
                            {selectedImage.color_arousal !== undefined && (
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-slate-400">Arousal (Energy)</span>
                                  <span className={`text-sm font-mono font-bold ${
                                    selectedImage.color_arousal > 0 ? 'text-orange-400' : 'text-blue-400'
                                  }`}>
                                    {selectedImage.color_arousal > 0 ? '+' : ''}{selectedImage.color_arousal.toFixed(2)}
                                  </span>
                                </div>
                                <div className="relative h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-500 z-10" />
                                  <div 
                                    className={`absolute h-full ${selectedImage.color_arousal > 0 ? 'bg-orange-500' : 'bg-blue-500'}`}
                                    style={{ 
                                      left: selectedImage.color_arousal < 0 ? `${50 + (selectedImage.color_arousal * 50)}%` : '50%',
                                      width: `${Math.abs(selectedImage.color_arousal * 50)}%` 
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {selectedImage.color_dominance !== undefined && (
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-slate-400">Dominance (Control)</span>
                                  <span className={`text-sm font-mono font-bold ${
                                    selectedImage.color_dominance > 0 ? 'text-red-400' : 'text-teal-400'
                                  }`}>
                                    {selectedImage.color_dominance > 0 ? '+' : ''}{selectedImage.color_dominance.toFixed(2)}
                                  </span>
                                </div>
                                <div className="relative h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-500 z-10" />
                                  <div 
                                    className={`absolute h-full ${selectedImage.color_dominance > 0 ? 'bg-red-500' : 'bg-teal-500'}`}
                                    style={{ 
                                      left: selectedImage.color_dominance < 0 ? `${50 + (selectedImage.color_dominance * 50)}%` : '50%',
                                      width: `${Math.abs(selectedImage.color_dominance * 50)}%` 
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {selectedImage.color_pleasure !== undefined && (
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-slate-400">Pleasure (Valence)</span>
                                  <span className={`text-sm font-mono font-bold ${
                                    selectedImage.color_pleasure > 0 ? 'text-green-400' : 'text-slate-400'
                                  }`}>
                                    {selectedImage.color_pleasure > 0 ? '+' : ''}{selectedImage.color_pleasure.toFixed(2)}
                                  </span>
                                </div>
                                <div className="relative h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-500 z-10" />
                                  <div 
                                    className={`absolute h-full ${selectedImage.color_pleasure > 0 ? 'bg-green-500' : 'bg-slate-500'}`}
                                    style={{ 
                                      left: selectedImage.color_pleasure < 0 ? `${50 + (selectedImage.color_pleasure * 50)}%` : '50%',
                                      width: `${Math.abs(selectedImage.color_pleasure * 50)}%` 
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-[9px] text-slate-500 italic mt-3 leading-relaxed">
                            Color psychology dimensions measuring emotional impact: Arousal (stimulating vs calming), 
                            Dominance (powerful vs submissive), and Pleasure (positive vs negative affect).
                          </p>
                        </div>
                      )}  

                      {/* Composition Density */}
                      {(selectedImage.negative_space_ratio !== undefined || selectedImage.occupied_space_ratio !== undefined || selectedImage.face_count !== undefined) && (
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3 flex items-center">
                            <Layers size={14} className="mr-2 text-cyan-400" /> Composition Density
                          </h3>
                          
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            {selectedImage.occupied_space_ratio !== undefined && (
                              <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                                <span className="text-[9px] text-slate-500 block mb-1">Occupied Space</span>
                                <span className="text-lg font-bold text-white">
                                  {Math.round(selectedImage.occupied_space_ratio * 100)}%
                                </span>
                              </div>
                            )}
                            
                            {selectedImage.negative_space_ratio !== undefined && (
                              <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30">
                                <span className="text-[9px] text-slate-500 block mb-1">Negative Space</span>
                                <span className="text-lg font-bold text-white">
                                  {Math.round(selectedImage.negative_space_ratio * 100)}%
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {selectedImage.face_count !== undefined && (
                            <div className="bg-slate-900/50 p-2 rounded border border-slate-700/30 flex justify-between items-center">
                              <span className="text-xs text-slate-400">Detected Faces</span>
                              <div className="flex items-center gap-2">
                                <Users size={14} className="text-cyan-400" />
                                <span className="text-sm font-bold text-white">{selectedImage.face_count}</span>
                              </div>
                            </div>
                          )}
                          
                          {selectedImage.text_density_ratio !== undefined && (
                            <div className="bg-slate-900/50 p-2 rounded border border-slate-700/30 flex justify-between items-center mt-2">
                              <span className="text-xs text-slate-400">Text Density</span>
                              <span className="text-sm font-bold text-white font-mono">
                                {selectedImage.text_density_ratio.toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          <p className="text-[9px] text-slate-500 italic mt-3 leading-relaxed">
                            Spatial analysis showing visual breathing room and element density. High occupied space suggests complexity; 
                            higher negative space indicates minimalism and focus.
                          </p>
                        </div>
                      )}

                        {/* Palette & Entropy */}
                        {selectedImage.dominant_colors_hex && (
                            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                                <div className="flex justify-between items-center mb-3">
                                   <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center">
                                      <Palette size={14} className="mr-2 text-pink-400" /> Palette & Entropy
                                   </h3>
                                   {copiedColor && (
                                     <span className="text-xs text-green-400 flex items-center animate-pulse">
                                       <CheckCircle size={12} className="mr-1"/> Copied {copiedColor}
                                     </span>
                                   )}
                                </div>
                                <div className="flex h-12 rounded-lg overflow-hidden border border-slate-700 mb-4">
                                    {selectedImage.dominant_colors_hex.map((hex, i) => (
                                        <div 
                                            key={i} 
                                            onClick={() => handleCopyColor(hex)}
                                            className="flex-1 hover:flex-[2] transition-all duration-300 cursor-pointer relative group"
                                            style={{ backgroundColor: hex }}
                                            title={hex}
                                        >
                                            <div className="opacity-0 group-hover:opacity-100 absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[1px]">
                                                <Copy size={16} className="text-white drop-shadow-md" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                                    <div className="bg-slate-900/50 p-2 rounded flex justify-between">
                                        <span>Warmth Idx</span> 
                                        <span className="text-slate-200 font-mono">{selectedImage.color_warmth_index?.toFixed(2)}</span>
                                    </div>
                                    <div className="bg-slate-900/50 p-2 rounded flex justify-between">
                                        <span>Contrast RMS</span> 
                                        <span className="text-slate-200 font-mono">{selectedImage.color_contrast_rms?.toFixed(2)}</span>
                                    </div>
                                    <div className="bg-slate-900/50 p-2 rounded flex justify-between">
                                        <span>Entropy (Noise)</span> 
                                        <span className="text-slate-200 font-mono">{selectedImage.color_entropy?.toFixed(2) || "0.00"}</span>
                                    </div>
                                    <div className="bg-slate-900/50 p-2 rounded flex justify-between">
                                        <span>Dynamic Rng</span> 
                                        <span className="text-slate-200 font-mono">{selectedImage.color_dynamic_range?.toFixed(2) || "0.00"}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                  )}

                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-slate-700 flex gap-3 bg-slate-900 z-10">
                  <a
                    download
                    href={`https://us-central1-knoesis-dante.cloudfunctions.net/gallery_download_image?campaignID=${campaignId}&name=${getPath(selectedImage.displayable_url).split("/").pop()}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors font-semibold text-sm shadow-lg shadow-cyan-900/20"
                  >
                    <Download size={16} /> Download
                  </a>
                  <button onClick={() => handleDelete(selectedImage.id)} className="flex-none flex items-center justify-center p-3 bg-red-900/20 hover:bg-red-900/40 text-red-400 hover:text-red-300 border border-red-900/50 rounded-lg transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ImageUploadDialog isOpen={isUploadOpen} onClose={() => setIsUploadOpen(false)} campaignId={campaignId} />
    </div>
  );
}

export default ImageGallery;